/* ===================================================================
   THE LANDMARK ARTWORK — ported VERBATIM from
   tommylong-poc-survey-v17/src/js/survey-map.js:478-930.

   Every path `d` string, every coordinate and the order of every draw
   call is character-for-character the survey sheet's. The only thing
   that changed is the surface: the SVG helpers append <path> nodes to a
   <g>; these stroke and fill Path2D objects into a CanvasRenderingContext2D
   whose origin is already at the landmark's anchor and whose scale is
   already set. `g` is that 2D context throughout.

   Each entry is drawn in MAP units about its own point of ground, which
   stands at (0, 0) with the ground line at y = 4.5 (+y downward, as in
   SVG). `w` is the half-width the shadow is laid out from.

   ---- THE RESOLVED CSS -------------------------------------------
   Taken from tommylong-poc-survey-v17/src/css/styles.css, with the
   :root custom properties substituted for their literal values:
     --paper-hi #f7efdd   --ink #2e261c   --ink-2 #55483a
     --ink-3 #7d6d59      --sea-line #4a7079   --accent #96322a

     .sv-vig-fill    fill #f7efdd; no stroke; opacity .92
     .sv-vig-line    fill none; stroke #55483a; stroke-width 1;
                     stroke-linejoin round; non-scaling-stroke
     .sv-lm-shadow   fill none; stroke #7d6d59; stroke-width .8;
                     opacity .45; stroke-linecap round; non-scaling
     .sv-lm-ray      fill none; stroke #55483a; stroke-width .7;
                     opacity .5;  stroke-linecap round; non-scaling
     .sv-lm-water    fill none; stroke #4a7079; stroke-width .8;
                     opacity .6;  non-scaling-stroke
     .sv-lm-smoke    fill none; stroke #7d6d59; stroke-width .7;
                     opacity .42; stroke-linecap round; non-scaling
                     (the CSS also drifts it on a 7s animation; a
                     canvas frame is a still, so no drift is applied)
     .sv-ship-hull   fill #f7efdd; stroke #3b3025; stroke-width .9
     .sv-ship-line   fill none;    stroke #3b3025; stroke-width .8
     .sv-ship-sail   fill #f7efdd; stroke #3b3025; stroke-width .75;
                     opacity .95
     .sv-ship-flag   fill #96322a; no stroke
     .sv-bed .sv-vig-line   opacity .78  (a wreck, seen through water)
     .sv-afloat      no paint of its own — a marker the relief reads
     .sv-lm-body     transform: scale(1.85 / z) — the APPARENT-SIZE hold.
                     That belongs to placement, not to artwork, so the
                     caller sets it in the context's scale before calling.

   `vector-effect: non-scaling-stroke` on the sheet means the ink is one
   screen pixel whatever the zoom. Canvas has no such property, so the
   line width is divided by the context's current scale to reproduce it.
   Set RoomLmArt.nonScalingStroke = false to get plain, scaling ink.
   =================================================================== */
(function (root) {
  'use strict';

  /* ---- the ink, one table, resolved ------------------------------ */
  var STYLE = {
    'sv-vig-fill':  { fill: '#f7efdd', alpha: 0.92 },
    'sv-vig-line':  { stroke: '#55483a', width: 1,    join: 'round' },
    'sv-lm-shadow': { stroke: '#7d6d59', width: 0.8,  alpha: 0.45, cap: 'round' },
    'sv-lm-ray':    { stroke: '#55483a', width: 0.7,  alpha: 0.5,  cap: 'round' },
    'sv-lm-water':  { stroke: '#4a7079', width: 0.8,  alpha: 0.6 },
    'sv-lm-smoke':  { stroke: '#7d6d59', width: 0.7,  alpha: 0.42, cap: 'round' },
    'sv-ship-hull': { fill: '#f7efdd', stroke: '#3b3025', width: 0.9 },
    'sv-ship-line': { stroke: '#3b3025', width: 0.8 },
    'sv-ship-sail': { fill: '#f7efdd', stroke: '#3b3025', width: 0.75, alpha: 0.95 },
    'sv-ship-flag': { fill: '#96322a' },
    /* BT2: the boats by life phase want three more inks, all out of this
       table's own four: a SAIL in the flag's red (the Mirror dinghy's), a
       solid of the ship's own dark (a funnel's top, a bulrush's head), and
       the red margin rule of an exercise book (the paper boat's page). */
    'sv-ship-red':  { fill: '#96322a', stroke: '#3b3025', width: 0.75, alpha: 0.92 },
    'sv-ship-ink':  { fill: '#3b3025', alpha: 0.9 },
    'sv-lm-margin': { stroke: '#96322a', width: 0.6, alpha: 0.5 },
    /* VY2: and one more, the basket's straw, so wicker reads as wicker at
       sailing size (the sheet's own sepia, paled) */
    'sv-ship-wicker': { fill: '#d8bd8a', stroke: '#3b3025', width: 0.9 },
    /* X11: an engraving has no light in it; what it has is PAPER. A light
       that is burning, and a bank that dries, are both drawn by leaving
       the paper. (The reasoning is in voyage.js's X11 block.) */
    'sv-lm-spark':  { fill: '#f7efdd', alpha: 0.95 },
    'sv-lm-glow':   { fill: '#f7efdd', alpha: 0.3 },
    'sv-lm-sand':   { fill: '#f7efdd', alpha: 0.33 }
  };

  var api = { nonScalingStroke: true };
  /* a wreck is drawn inside .sv-bed, which lightens its outline */
  var bedMode = false;

  var pathCache = Object.create(null);
  function P(d) {
    var p = pathCache[d];
    if (!p) p = pathCache[d] = new Path2D(d);
    return p;
  }

  /* one screen pixel of ink whatever the zoom: non-scaling-stroke */
  function lineWidth(g, w) {
    if (!api.nonScalingStroke) return w;
    var m = g.getTransform ? g.getTransform() : null;
    if (!m) return w;
    var s = Math.sqrt(Math.abs(m.a * m.d - m.b * m.c));
    return s > 1e-6 ? w / s : w;
  }

  function paint(g, path, cls) {
    var st = STYLE[cls];
    if (!st) return;
    var a = st.alpha === undefined ? 1 : st.alpha;
    if (bedMode && cls === 'sv-vig-line') a = 0.78;
    var prev = g.globalAlpha;
    g.save();
    g.globalAlpha = prev * a;
    if (st.fill) { g.fillStyle = st.fill; g.fill(path); }
    if (st.stroke) {
      g.strokeStyle = st.stroke;
      g.lineWidth = lineWidth(g, st.width);
      g.lineJoin = st.join || 'miter';
      g.lineCap = st.cap || 'butt';
      g.stroke(path);
    }
    g.restore();
  }

  function lmPath(g, d, cls) { paint(g, P(d), cls); return g; }
  function lmFill(g, d) { return lmPath(g, d, 'sv-vig-fill'); }
  function lmLine(g, d) { return lmPath(g, d, 'sv-vig-line'); }
  /* Fill and outline from one figure, which is how every one of these is
     built: the paper shows through the ink, as it does in an engraving. */
  function lmSolid(g, d) { lmFill(g, d); lmLine(g, d); return g; }

  /* the few entries cut with a circle or an ellipse rather than a path */
  function lmCircle(g, cx, cy, r, cls) {
    var p = new Path2D();
    p.arc(cx, cy, r, 0, Math.PI * 2);
    paint(g, p, cls);
  }
  function lmEllipse(g, cx, cy, rx, ry, cls) {
    var p = new Path2D();
    p.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
    paint(g, p, cls);
  }

  /* =================================================================
     WHICH LANDMARK an event gets is either DATA or a RULE: a `landmark`
     named on the event in src/_data/events.json wins, and where there is
     none the event's TYPE supplies one (LM_DEFAULT). Bespoke inputs are
     data, everything else is a rule — the standing arrangement since v15.
     ================================================================= */
  /* ==== BT2 (2026-09-26): THE YARD AND THE WRECK NOW SHARE THE STEAMSHIP
     (`steamer`, below) and not these strokes; these stay because the
     sheet's own wreck at every named deep (marks.js) is drawn in them.
     ==== V2: ONE HULL, TWO MARKS. The voyage's 2017 mark is the yard the
     2022 wreck was built in (EGGS.md section 4; Tommy: *"could be quite a
     neat touch if the wreck we saw is a boat that's come from the harbour
     of the founding"*, and the pun is his too: *"there might also be a pun
     to be found in ship and shipping, like shipping code"*). The
     relationship is the DRAWING and the two cards and nothing else — so
     the hull is not drawn twice. These are the wreck's own strokes, lifted
     out of its entry unchanged, and the shipyard stands the same figure
     upright on the stocks, a quarter bigger because JDLT was twelve people
     and the lugger is one. ==== */
  var HULL_D = 'M-7 0q3.4 -3 8.6 -2.6q3.6 0.4 5.4 2.6q-1.8 2.2 -5.4 2.6q-5.2 0.4 -8.6 -2.6Z';
  var HULL_KEEL = 'M-6.2 0H5.4';
  var HULL_RIBS = 'M-4.2 -2V1.2M-1.6 -2.5V2.4M1 -2.5V0.6M3.6 -2.2V2.1';

  /* LM1: A TUBE along a centreline — a smooth curve through `pts`
     (Catmull-Rom, sampled), its outline `w` wide as one closed path `d`
     for lmSolid, and `lay`: short slanted cuts across it, the lay of an
     armoured cable, for a light hatch. Art units in, path strings out. */
  function tube(pts, w) {
    var c = [], i, t, n = pts.length;
    function at(j) { return pts[Math.max(0, Math.min(n - 1, j))]; }
    for (i = 0; i < n - 1; i++) {
      var p0 = at(i - 1), p1 = at(i), p2 = at(i + 1), p3 = at(i + 2);
      for (t = 0; t < 1; t += 0.25) {
        var t2 = t * t, t3 = t2 * t;
        c.push([0.5 * (2 * p1[0] + (-p0[0] + p2[0]) * t + (2 * p0[0] - 5 * p1[0] + 4 * p2[0] - p3[0]) * t2 + (-p0[0] + 3 * p1[0] - 3 * p2[0] + p3[0]) * t3),
                0.5 * (2 * p1[1] + (-p0[1] + p2[1]) * t + (2 * p0[1] - 5 * p1[1] + 4 * p2[1] - p3[1]) * t2 + (-p0[1] + 3 * p1[1] - 3 * p2[1] + p3[1]) * t3)]);
      }
    }
    c.push(pts[n - 1]);
    var L = [], R = [], lay = '', h = w / 2;
    for (i = 0; i < c.length; i++) {
      var a = c[Math.max(0, i - 1)], b = c[Math.min(c.length - 1, i + 1)];
      var dx = b[0] - a[0], dy = b[1] - a[1], m = Math.sqrt(dx * dx + dy * dy) || 1;
      var nx = -dy / m * h, ny = dx / m * h;
      L.push([c[i][0] + nx, c[i][1] + ny]); R.push([c[i][0] - nx, c[i][1] - ny]);
      if (i > 0 && i < c.length - 1)
        lay += 'M' + (c[i][0] + nx * 0.8 - dx / m * 0.35).toFixed(2) + ' ' + (c[i][1] + ny * 0.8 - dy / m * 0.35).toFixed(2) +
               'L' + (c[i][0] - nx * 0.8 + dx / m * 0.35).toFixed(2) + ' ' + (c[i][1] - ny * 0.8 + dy / m * 0.35).toFixed(2);
    }
    var d = 'M' + L.map(function (q) { return q[0].toFixed(2) + ' ' + q[1].toFixed(2); }).join('L') +
            'L' + R.reverse().map(function (q) { return q[0].toFixed(2) + ' ' + q[1].toFixed(2); }).join('L') + 'Z';
    return { d: d, lay: lay };
  }

  /* ==== BT2 (2026-09-26): THE STEAMSHIP, ONE FIGURE FOR THREE DRAWINGS.
     Tommy: "any changes we make to the shipwreck would need to change the
     2018 launch of that same boat". So the ship JDLT was is drawn ONCE,
     here, and three entries stand it up: the boat she sails from the Wreck
     to the Shipyard (`steamship`, smoking), the same hull on the stocks at
     the Shipyard (`shipyard`), and the same hull broken on the rocks at
     the Wreck (`steamer-wreck`, her lifeboat gone off the davits — the
     boat she came into the Wreck in). A small coastal steamer, side on,
     bow to the WEST like every boat here (her bowsprit side is the left):
     a raked stem, a counter stern, the bridge house amidships, one funnel
     with the red band, two masts with the aerial between them, and her
     lifeboat on davits abaft the funnel. Ship's units: waterline and keel
     at y 4.3, stem head at x -13, the ensign's fly at x 14.7.
       o.smoke   — smoke from the funnel, streaming aft (under way only)
       o.boat    — the lifeboat on its davits (false: the falls hang empty)
       o.flag    — the ensign at the stern
       o.stump   — the mainmast snapped off above the crosstrees
       o.part    — 'hull' draws the hull alone, for a clip to cut in two */
  var STEAMER_HULL = 'M-13 -0.4L-11.3 4.3H10.2Q12.1 4.2 12.8 0.6Q0.5 2.6 -13 -0.4Z';
  function steamer(g, o) {
    o = o || {};
    // the rigging first, so every solid stands in front of it
    lmPath(g, 'M-7.8 -9.4L-13 -0.4M11.3 -8L12.8 0.6' +
              (o.stump ? '' : 'M-7.8 -9.2L11.3 -7.8'), 'sv-ship-line');
    lmPath(g, 'M-8.2 0.8L-7.8 -9.4M-8.6 -7.4H-7.1M-8 -0.6L-11.4 -3.4M-7.95 -5.5L-11.4 -3.4', 'sv-ship-line');
    if (o.stump) {
      lmPath(g, 'M10.9 1.2L11.15 -4.2M10.5 -2.6H11.9', 'sv-ship-line');
      lmPath(g, 'M11.15 -4.2l0.25 -0.7 0.2 0.5', 'sv-ship-line');       // the break
    } else {
      lmPath(g, 'M10.9 1.2L11.3 -8M10.5 -6.4H12', 'sv-ship-line');
    }
    // the funnel, raked aft, its red band and its black top
    lmPath(g, 'M3.6 1.4L4.3 -6.3H6.3L5.6 1.4Z', 'sv-ship-hull');
    lmPath(g, 'M4.06 -3.7L4.16 -4.7H6.16L6.06 -3.7Z', 'sv-ship-flag');
    lmPath(g, 'M4.23 -5.5L4.3 -6.3H6.3L6.23 -5.5Z', 'sv-ship-ink');
    if (o.smoke) lmPath(g, 'M5.4 -6.7q1.2 -1.4 3 -1.1t3.2 -1.6q1.4 -0.7 2.8 -0.2M5.9 -6.9q1.6 -2 3.6 -1.7t2.6 -1.4', 'sv-lm-smoke');
    // the house amidships, the bridge on it, its windows, the awning
    lmPath(g, 'M-3.4 1.6V-0.9H3V1.6Z', 'sv-ship-hull');
    lmPath(g, 'M-2.8 -0.9V-2.9H0.9V-0.9Z', 'sv-ship-hull');
    lmPath(g, 'M-3.7 -2.9H1.3M-1.2 -2.9V-3.9M-2.3 -2.4V-1.6M-1.5 -2.4V-1.6M-0.7 -2.4V-1.6M0.1 -2.4V-1.6' +
              'M-2.6 0.1h0.7M-1.2 0.1h0.7M0.2 0.1h0.7M1.6 0.1h0.7', 'sv-ship-line');
    // a cowl ventilator abaft the funnel, and the hatch in the fore well
    lmPath(g, 'M7 1.3V-0.6q0 -0.7 -0.7 -0.7M-6.4 1V0.3H-4.4V1.1', 'sv-ship-line');
    // the davits, and her lifeboat in them — or the falls hanging empty
    lmPath(g, 'M7.4 1.2V-1q0 -0.5 0.5 -0.5M10.1 1.2V-1q0 -0.5 -0.5 -0.5', 'sv-ship-line');
    if (o.boat === false) lmPath(g, 'M7.9 -1.5V0.2M9.6 -1.5V0.5q0.2 0.4 -0.1 0.6', 'sv-ship-line');
    else {
      lmPath(g, 'M7.6 -0.5H9.9q-0.3 0.9 -1.15 0.9t-1.15 -0.9Z', 'sv-ship-hull');
      lmPath(g, 'M7.9 -1.5V-0.5M9.6 -1.5V-0.5', 'sv-ship-line');
    }
    if (o.flag) {
      lmPath(g, 'M12.7 0.7L13.1 -1.7', 'sv-ship-line');
      lmPath(g, 'M13.1 -1.7l1.6 0.35 -1.5 0.6z', 'sv-ship-flag');
    }
    steamerHull(g);
  }
  // the hull on its own: fill, the boot-top, the bottom's hatch, the ports
  function steamerHull(g) {
    lmPath(g, STEAMER_HULL, 'sv-ship-hull');
    var h = '', x;
    for (x = -10.9; x < 10.1; x += 0.8) h += 'M' + x.toFixed(1) + ' 3.2V4.2';
    lmPath(g, h, 'sv-lm-ray');
    lmPath(g, 'M-11.72 3.1H11.9', 'sv-ship-line');
    for (x = -9.6; x <= 9.7; x += 2.4) lmCircle(g, x, 2.05, 0.3, 'sv-ship-line');
  }

  /* VY2: `weave` (BT2's hairline basketwork) went with the old basket */
  /* the rim, as a basket's rim is finished: a bound rod, the binding
     wrapped round it on the slant */
  function rim(g, rx, ry, cy, n) {
    var d = '', i;
    for (i = 1; i < n; i++) {
      var a = Math.PI * (1 - i / n), x = Math.cos(a) * rx, y = cy + Math.sin(a) * ry;
      d += 'M' + (x - 0.25).toFixed(2) + ' ' + (y - 0.3).toFixed(2) + 'L' + (x + 0.25).toFixed(2) + ' ' + (y + 0.3).toFixed(2);
    }
    lmPath(g, d, 'sv-ship-line');
  }

  /* VY2 (2026-09-26): `reedRand` and `reedStand` went with the reeds */

  var LANDMARKS = {
    /* the two that were already here: a short break and a week away */
    tent: { w: 5, draw: function (g) {
      lmFill(g, 'M-5 4.5L0 -5L5 4.5Z');
      lmLine(g, 'M-5 4.5L0 -5L5 4.5M0 -5v9.5');
    } },
    house: { w: 5.5, draw: function (g) {
      lmFill(g, 'M-5 4.5V-1.4L0 -5.6L5 -1.4V4.5Z');
      lmLine(g, 'M-6.2 -1.1L0 -6.4L6.2 -1.1');
      lmLine(g, 'M-5 4.5V-1.4L0 -5.6L5 -1.4V4.5Z');
      lmLine(g, 'M3 -3.2V-5.6h1.6v3.2');
    } },

    /* --- type defaults ------------------------------------------- */
    /* A WORK TRIP IS A LIGHTHOUSE: you went somewhere to shine a light. */
    lighthouse: { w: 4.5, draw: function (g) {
      lmSolid(g, 'M-3.1 4.5L-2 -3.9H2L3.1 4.5Z');
      lmLine(g, 'M-2.6 1.2H2.6M-2.3 -1.4H2.3');
      lmSolid(g, 'M-2.5 -3.9V-6.2H2.5V-3.9Z');
      lmLine(g, 'M-2.9 -6.2H2.9M0 -6.2L0 -8.2M-1.7 -6.9L1.7 -6.9');
      lmLine(g, 'M-2.1 -8.3L0 -9.6L2.1 -8.3');
      // the light itself, thrown both ways along the coast
      lmPath(g, 'M-4.4 -5.4L-8 -6.6M-4.4 -4.6L-8 -3.9M4.4 -5.4L8 -6.6M4.4 -4.6L8 -3.9', 'sv-lm-ray');
      lmSolid(g, 'M-4.6 4.5H4.6L3.7 2.6H-3.7Z');
    } },
    /* QUARTERLY PLANNING IS A STONE CIRCLE: the tribes gather and argue
       about the next quarter. Drawn as a moot in perspective — the far
       stones smaller and higher on the paper than the near ones. */
    'stone-circle': { w: 8, draw: function (g) {
      lmLine(g, 'M-8.4 3.4q8.4 3 16.8 0');
      [[-6.6, 3.9, 1.5, 4.4], [-2.4, 4.5, 1.7, 5.2], [2.4, 4.5, 1.7, 5.2], [6.6, 3.9, 1.5, 4.4],
       [-4.6, 1.1, 1.2, 3.4], [0, 0.6, 1.3, 3.6], [4.6, 1.1, 1.2, 3.4]].forEach(function (s) {
        var x = s[0], y = s[1], hw = s[2], h = s[3];
        lmSolid(g, 'M' + (x - hw) + ' ' + y + 'L' + (x - hw * 0.72) + ' ' + (y - h) +
          'L' + (x + hw * 0.72) + ' ' + (y - h) + 'L' + (x + hw) + ' ' + y + 'Z');
      });
    } },
    /* AN AWS SUMMIT IS A MARQUEE: a big tent with a pennant on it. */
    marquee: { w: 7, draw: function (g) {
      lmSolid(g, 'M-7 0.2L0 -5.4L7 0.2Z');
      lmLine(g, 'M-7 0.2q1.75 1.7 3.5 0q1.75 1.7 3.5 0q1.75 1.7 3.5 0q1.75 1.7 3.5 0');
      lmLine(g, 'M-5.6 1.4V4.5M5.6 1.4V4.5M0 -5.4V-8.8');
      lmLine(g, 'M-3.5 -1.3L0 -5.4L3.5 -1.3');
      lmSolid(g, 'M0 -8.8L3.9 -7.7L0 -6.6Z');
    } },
    /* A BANK HOLIDAY IS A FEAST DAY: a church with a spire. Cut only where
       water meets land — the v9 landing rule, unchanged. */
    church: { w: 5.5, draw: function (g) {
      lmSolid(g, 'M-5.4 4.5V-0.6H0.8V4.5Z');
      lmLine(g, 'M-3.9 4.5V1.6h1.6v2.9');
      lmSolid(g, 'M0.8 4.5V-3.6H4.2V4.5Z');
      lmLine(g, 'M0.4 -3.6H4.6');
      lmSolid(g, 'M0.4 -3.6L2.5 -8.6L4.6 -3.6Z');
      lmLine(g, 'M2.5 -8.6V-10.2M1.7 -9.6H3.3');
    } },

    /* --- named landmarks, from events.json ------------------------ */
    /* ROME: a pair of ruined columns under a broken architrave. */
    'ruined-columns': { w: 6, draw: function (g) {
      lmSolid(g, 'M-5 3.6V-3.4H-2.4V3.6Z');
      lmSolid(g, 'M1.6 3.6V-3.4H4.2V3.6Z');
      lmLine(g, 'M-4.2 3.2V-3M-3.4 3.2V-3M2.4 3.2V-3M3.4 3.2V-3');
      lmSolid(g, 'M-5.6 -3.4H-1.8V-5.2H-5.6ZM1 -3.4H4.8V-5.2H1Z');
      lmLine(g, 'M-1.8 -4.4L-0.4 -4.9L0.4 -4.2L1 -4.7');
      lmLine(g, 'M-6.2 3.6H5.4');
      lmSolid(g, 'M5.6 3.6L7.4 2.4L8.6 3.6Z');
    } },
    /* NAPLES: Vesuvius, a cone with a thread of smoke off the rim. The
       smoke is a standing sprite and may drift; the GROUND never moves. */
    vesuvius: { w: 8, draw: function (g) {
      lmSolid(g, 'M-8 4.5L-2.2 -4.2H2.2L8 4.5Z');
      lmLine(g, 'M-2.2 -4.2q2.2 1.4 4.4 0');
      lmLine(g, 'M-4.6 -0.7L-3.1 1.4M3.4 -1.4L5 0.8M-0.6 0.4L0.6 2.2');
      lmPath(g, 'M-0.6 -4.9q-2.4 -2.1 -0.4 -3.8q2 -1.7 0.2 -3.6', 'sv-lm-smoke');
      lmPath(g, 'M1.2 -5.1q-1.4 -2.6 0.6 -4', 'sv-lm-smoke');
    } },
    /* PORTO: a rabelo, the flat-bottomed boat that brought the pipes down
       the Douro. She FLOATS (v23, C): her point of ground is the water
       surface at the datum, never the bed and never a rock, and she wants
       no shadow, only her ripple. */
    rabelo: { w: 7, noShadow: true, floats: true, draw: function (g) {
      lmSolid(g, 'M-7 1.6q7 2.6 14 0L11 3.9q-9 1.7-18 0Z');
      lmLine(g, 'M0 1.9V-6.4');
      lmSolid(g, 'M0.5 -6q5 2.3 0 5.2Z');
      lmLine(g, 'M-0.6 -6.4L-5.4 -0.6');
      lmLine(g, 'M2.8 2.2L7.6 -0.4');
      lmLine(g, 'M-4.2 1.9a1.2 1.2 0 0 1 2.4 0M-1.4 2a1.2 1.2 0 0 1 2.4 0');
    } },
    /* LISBON: a tram climbing a hill. */
    tram: { w: 6.5, draw: function (g) {
      lmLine(g, 'M-9 6.2L9 2.4');
      /* the car itself, set on the gradient: rotate(-12) */
      g.save();
      g.rotate(-12 * Math.PI / 180);
      var b = g;
      lmSolid(b, 'M-5 2.6V-2.6H5V2.6Z');
      lmLine(b, 'M-5.8 -2.6H5.8');
      lmLine(b, 'M-3.6 -1.6H-1.4V0.4H-3.6ZM-0.4 -1.6H1.8V0.4H-0.4Z');
      lmLine(b, 'M3 -1.6H4.2V0.4H3Z');
      lmLine(b, 'M1.2 -2.6L3.6 -6.2M3.6 -6.2H5.6');
      lmCircle(b, -3, 3.4, 0.9, 'sv-vig-line');
      lmCircle(b, 3, 3.4, 0.9, 'sv-vig-line');
      g.restore();
    } },
    /* BRISTOL: the suspension bridge over its gorge — two towers, the
       catenary, and the rock falling away on both sides. */
    'suspension-bridge': { w: 9, noShadow: true, draw: function (g) {
      lmSolid(g, 'M-6.2 0.4V-6.4H-4V0.4Z');
      lmSolid(g, 'M4 0.4V-6.4H6.2V0.4Z');
      lmLine(g, 'M-5.1 -6.4q5.1 6.2 10.2 0');
      lmLine(g, 'M-5.1 -4q-2.4 0.5 -3.8 1.4M5.1 -4q2.4 0.5 3.8 1.4');
      lmLine(g, 'M-9 -1.2H9');
      lmLine(g, 'M-2.6 -3.4V-1.2M0 -4.4V-1.2M2.6 -3.4V-1.2');
      /* NO GORGE WALLS DRAWN. They were four strokes falling away from the
         deck on both sides, and at this size they read as the legs of an
         insect rather than as rock; the ground the bridge stands on — the
         skerry, or the coast itself — is the gorge. */
      lmLine(g, 'M-9 -1.2v1.6M9 -1.2v1.6');
    } },
    /* BATH: a colonnade round a pool, with the steam coming off it. */
    'roman-bath': { w: 8, draw: function (g) {
      lmEllipse(g, 0, 3.2, 6.4, 1.9, 'sv-vig-fill');
      lmEllipse(g, 0, 3.2, 6.4, 1.9, 'sv-vig-line');
      lmLine(g, 'M-4.4 3.6q4.4 1 8.8 0M-3.2 2.4q3.2 -0.9 6.4 0');
      [-6.6, -2.2, 2.2, 6.6].forEach(function (x) {
        lmSolid(g, 'M' + (x - 0.9) + ' 0.6V-4.6H' + (x + 0.9) + 'V0.6Z');
        lmLine(g, 'M' + (x - 1.4) + ' -4.6H' + (x + 1.4));
      });
      lmLine(g, 'M-8 -5.4H8M-8 -5.4L-7.4 -6.6H7.4L8 -5.4');
      lmPath(g, 'M-2 1.6q-1.6 -2 0.2 -3.4M2.2 1.6q-1.6 -2 0.2 -3.4', 'sv-lm-smoke');
    } },
    /* THE COTSWOLDS: a dry-stone wall, and three sheep in front of it. */
    'drystone-wall': { w: 9, draw: function (g) {
      lmSolid(g, 'M-9 3.4V-0.8H9V3.4Z');
      lmLine(g, 'M-9 1.3H9M-6.2 -0.8V1.3M-2.6 -0.8V1.3M1 -0.8V1.3M5 -0.8V1.3' +
        'M-7.4 1.3V3.4M-4 1.3V3.4M-0.6 1.3V3.4M3.2 1.3V3.4M6.6 1.3V3.4');
      lmLine(g, 'M-9 -0.8q1.5 -1.2 3 0q1.5 -1.2 3 0q1.5 -1.2 3 0q1.5 -1.2 3 0q1.5 -1.2 3 0q1.5 -1.2 3 0');
      [[-5.4, 0], [0.4, 0.4], [5.6, 0]].forEach(function (s) {
        var x = s[0], y = 4.2 + s[1];
        lmEllipse(g, x, y - 0.9, 1.9, 1.2, 'sv-vig-fill');
        lmEllipse(g, x, y - 0.9, 1.9, 1.2, 'sv-vig-line');
        lmLine(g, 'M' + (x + 1.7) + ' ' + (y - 1.7) + 'a0.85 0.85 0 1 1 0.9 0.5');
        lmLine(g, 'M' + (x - 1.1) + ' ' + y + 'v1M' + (x + 0.9) + ' ' + y + 'v1');
      });
    } },
    /* SIZEWELL B: the dome. A lighthouse-shaped thing by the sea. */
    dome: { w: 6.5, draw: function (g) {
      lmSolid(g, 'M-4.6 0.6a4.6 4.6 0 0 1 9.2 0Z');
      lmLine(g, 'M-2.6 0.2a2.6 4.4 0 0 1 5.2 0M0 -4V0.4');
      lmSolid(g, 'M-6 0.6H6V3.4H-6Z');
      lmLine(g, 'M-6 2H6');
      lmSolid(g, 'M5.4 0.6V-3.4H7V0.6Z');
      lmLine(g, 'M-7.4 3.4H7.4M-9 4.6q9 1.2 18 0');
    } },
    /* REYKJAVIK: a geyser mid-spout. */
    geyser: { w: 6, draw: function (g) {
      lmSolid(g, 'M-6.4 4.5q6.4 -3.4 12.8 0Z');
      lmLine(g, 'M-3.4 3.2q3.4 -1.4 6.8 0');
      lmSolid(g, 'M-1.8 2.2q-0.6 -6.2 1.8 -9.4q2.4 3.2 1.8 9.4Z');
      lmLine(g, 'M0 -6.4V1.4');
      lmPath(g, 'M-4 -2.2L-5.8 -3.8M4.2 -2.6L6 -4.2M-3.4 -5.4L-4.6 -6.6M3.6 -5.6L4.8 -6.8', 'sv-lm-ray');
    } },
    /* DEVON: a beach hut on the cliff path. The tent and the house stood
       here for four versions; this is the upgrade. */
    'beach-hut': { w: 5, draw: function (g) {
      lmSolid(g, 'M-4.2 4.5V-0.9H4.2V4.5Z');
      lmSolid(g, 'M-5.2 -0.9L0 -5L5.2 -0.9Z');
      lmLine(g, 'M-1.6 4.5V0.6H1.6V4.5Z');
      lmLine(g, 'M-3.2 -0.9V4.5M3.2 -0.9V4.5');
      lmLine(g, 'M-6.6 5.4q3.2 -0.9 6.2 -0.9');   // the path up to the door
    } },
    /* THORPE PARK (2026-10-15): a roller coaster, the lift hill, the drop
       and a loop, on its trestles. */
    coaster: { w: 9, draw: function (g) {
      lmLine(g, 'M-6.8 4.5V-2.4M-5.4 4.5V-6.2M-4 4.5V-9M-2.2 4.5V-5.4M-0.6 4.5V-0.6');
      lmLine(g, 'M-9 4.5L-4.4 -9.2Q-3.6 -10.4 -2.8 -9.2L0.6 2.2Q1.4 4.2 3.4 3.8');
      lmLine(g, 'M3.4 3.8a3.1 3.1 0 1 0 2.2 0.1L9 4.5');
      lmLine(g, 'M-9.4 4.5H9.4');
    } },
    /* VILNIUS: a belfry. */
    belfry: { w: 4, draw: function (g) {
      lmSolid(g, 'M-3.6 4.5V-1.2H3.6V4.5Z');
      lmLine(g, 'M-4.2 -1.2H4.2');
      lmSolid(g, 'M-2.7 -1.2V-5.4H2.7V-1.2Z');
      lmLine(g, 'M-1.3 -1.2V-3.6a1.3 1.3 0 0 1 2.6 0V-1.2');
      lmLine(g, 'M-3.3 -5.4H3.3');
      lmSolid(g, 'M-2.9 -5.4L0 -10.4L2.9 -5.4Z');
      lmLine(g, 'M0 -10.4V-12M-0.8 -11.4H0.8');
      lmLine(g, 'M-2.4 1.2H2.4');
    } },
    /* CHESTER: a stretch of the city wall, with a gate through it. */
    'city-wall': { w: 9, draw: function (g) {
      lmSolid(g, 'M-9 4.5V-1.6H9V4.5Z');
      var mer = '';
      [-9, -6.4, -3.8, 3.8, 6.4].forEach(function (x) {
        mer += 'M' + x + ' -1.6V-3.6H' + (x + 1.8) + 'V-1.6';
      });
      lmFill(g, mer + 'Z');
      lmLine(g, mer);
      lmLine(g, 'M-9 -1.6H9');
      lmLine(g, 'M-7.4 0.4H-5M-4 2.2H-6.4M5 0.4H7.4M6.4 2.2H4');
      lmFill(g, 'M-2 4.5V1.2a2 2 0 0 1 4 0V4.5Z');
      lmLine(g, 'M-2 4.5V1.2a2 2 0 0 1 4 0V4.5');
      lmSolid(g, 'M-3 -1.6V-4.6H3V-1.6Z');
    } },
    /* GDANSK: the Zuraw, the harbour crane on the Motlawa. */
    'harbour-crane': { w: 6, draw: function (g) {
      lmSolid(g, 'M-5.6 4.5V-2.4H2.4V4.5Z');
      lmSolid(g, 'M-6.4 -2.4L-1.6 -7L3.2 -2.4Z');
      lmLine(g, 'M-3.8 4.5V1.6H-1.4V4.5M0 -0.8H1.6');
      lmLine(g, 'M-1.6 -7V-8.2');
      lmSolid(g, 'M2 -5.6L7.6 -3.2L7 -1.9L1.4 -4.3Z');
      lmLine(g, 'M7.3 -2.6V1.2M6.3 1.2H8.3M7.3 1.2V2.6');
      lmLine(g, 'M6.7 2.6q0.6 1.2 1.2 0');
    } },
    /* CARCASSONNE: the Cité — the double curtain wall, the lower outer
       wall in front and the inner behind it, with the witch's-hat towers
       (the pointed slate roofs Viollet-le-Duc gave them) along both. */
    carcassonne: { w: 11, draw: function (g) {
      // the inner wall, higher, with its merlons
      lmSolid(g, 'M-8.6 1.4V-2.6H8.6V1.4Z');
      lmFill(g, 'M-8.6 -2.6V-3.4H-7.4V-2.6M-5.4 -2.6V-3.4H-4.2V-2.6M-2.2 -2.6V-3.4H-1V-2.6M1 -2.6V-3.4H2.2V-2.6M4.2 -2.6V-3.4H5.4V-2.6M7.4 -2.6V-3.4H8.6V-2.6Z');
      lmLine(g, 'M-8.6 -2.6V-3.4H-7.4V-2.6M-5.4 -2.6V-3.4H-4.2V-2.6M-2.2 -2.6V-3.4H-1V-2.6M1 -2.6V-3.4H2.2V-2.6M4.2 -2.6V-3.4H5.4V-2.6M7.4 -2.6V-3.4H8.6V-2.6');
      lmLine(g, 'M-8.6 -2.6H8.6');
      // three inner towers, round, under witch's hats
      [-6.2, 0, 6.2].forEach(function (x, i) {
        var top = i === 1 ? -4.2 : -3.6, tip = i === 1 ? -9.4 : -8.2;
        lmSolid(g, 'M' + (x - 1.4) + ' 1.4V' + top + 'H' + (x + 1.4) + 'V1.4Z');
        lmLine(g, 'M' + (x - 0.4) + ' -1.2V-0.2M' + (x + 0.4) + ' -1.2V-0.2');
        lmSolid(g, 'M' + (x - 1.9) + ' ' + top + 'L' + x + ' ' + tip + 'L' + (x + 1.9) + ' ' + top + 'Z');
        lmLine(g, 'M' + x + ' ' + tip + 'V' + (tip - 1.2));
      });
      // the outer wall, lower, in front, with its own two towers
      lmSolid(g, 'M-11 4.5V1.8H11V4.5Z');
      lmLine(g, 'M-11 1.8V1H-9.9V1.8M-8 1.8V1H-6.9V1.8M-5 1.8V1H-3.9V1.8M-2 1.8V1H-0.9V1.8M1 1.8V1H2.1V1.8M4 1.8V1H5.1V1.8M7 1.8V1H8.1V1.8M10 1.8V1H11V1.8');
      lmLine(g, 'M-11 1.8H11M-7.4 3.4H-5.8M3.6 3.4H5.2');
      [-9.6, 9.6].forEach(function (x) {
        lmSolid(g, 'M' + (x - 1.2) + ' 4.5V0.2H' + (x + 1.2) + 'V4.5Z');
        lmSolid(g, 'M' + (x - 1.7) + ' 0.2L' + x + ' -3.4L' + (x + 1.7) + ' 0.2Z');
      });
      // the gate through the outer wall
      lmFill(g, 'M-1.1 4.5V3a1.1 1.1 0 0 1 2.2 0V4.5Z');
      lmLine(g, 'M-1.1 4.5V3a1.1 1.1 0 0 1 2.2 0V4.5');
    } },
    /* AWS re:INVENT: the Luxor, a pyramid with its beam, in desert hatch. */
    pyramid: { w: 7, draw: function (g) {
      lmPath(g, 'M-1.1 -5.6L-2.6 -12.4M1.1 -5.6L2.6 -12.4', 'sv-lm-ray');
      lmSolid(g, 'M-7 4.5L0 -5.4L7 4.5Z');
      lmLine(g, 'M0 -5.4V4.5M-3.5 -0.5H0M0 -0.5H3.5');
      lmLine(g, 'M-11 5.6q2 -1.2 4 0M7.6 6q2 -1.2 4 0M-10 3.6q1.4 -0.9 2.8 0');
    } },

    /* ==== LM2 (2026-09-29): THE HOLIDAYS FROM NOVEMBER 2022 TO APRIL
       2025 (events.json, EV2). Thirteen more, cut in the same hand as the
       ones above: a silhouette first, a paper fill under a single ink, and
       no more strokes than the eye needs at a few millimetres. ==== */
    /* TOKYO: a torii — two posts leaning in, the kasagi on top with its
       ends swept up, the nuki through the posts below it, the strut
       between. */
    torii: { w: 6.5, draw: function (g) {
      lmSolid(g, 'M-4.5 4.5L-3.9 -4.6H-2.9L-3.3 4.5Z');
      lmSolid(g, 'M4.5 4.5L3.9 -4.6H2.9L3.3 4.5Z');
      lmSolid(g, 'M-4.9 4.5V3.7H-2.9V4.5ZM4.9 4.5V3.7H2.9V4.5Z');
      lmSolid(g, 'M-5.4 -2.8V-1.9H5.4V-2.8Z');
      lmSolid(g, 'M-0.45 -2.8V-4.7H0.45V-2.8Z');
      lmSolid(g, 'M-5.3 -4.7V-5.5H5.3V-4.7Z');
      lmSolid(g, 'M-7 -7.4Q0 -5.2 7 -7.4L6.4 -5.8Q0 -4.4 -6.4 -5.8Z');
    } },
    /* BARBADOS: a palm on the sand, the trunk bowed and ringed, the fronds
       thrown out and drooping, two nuts under the crown. */
    palm: { w: 5.5, draw: function (g) {
      lmLine(g, 'M-4.6 4.5q4.6 -1.6 9.6 0');
      var t = tube([[1.4, 4.1], [1.2, 0.6], [0.2, -3.2], [-1.3, -6.3]], 1.15);
      lmSolid(g, t.d);
      lmLine(g, t.lay);
      lmSolid(g, 'M-1.4 -6.6Q1.8 -6.8 3.2 -3.2Q1 -5.4 -1.4 -6.6Z');
      lmSolid(g, 'M-1.4 -6.6Q-4.2 -6.2 -5.2 -2.8Q-3.6 -5.4 -1.4 -6.6Z');
      lmSolid(g, 'M-1.4 -6.6Q2.8 -9.4 6.4 -5.4Q2.8 -7.6 -1.4 -6.6Z');
      lmSolid(g, 'M-1.4 -6.6Q-5.2 -9.2 -7.6 -5Q-5 -7.4 -1.4 -6.6Z');
      lmSolid(g, 'M-1.4 -6.6Q0.8 -10.4 4 -10Q1 -9.2 -1.4 -6.6Z');
      lmSolid(g, 'M-1.4 -6.6Q-3.2 -10.4 -5.8 -9.6Q-3.2 -9 -1.4 -6.6Z');
      lmCircle(g, -1.9, -5.9, 0.5, 'sv-vig-line');
      lmCircle(g, -0.9, -5.7, 0.5, 'sv-vig-line');
    } },
    /* PARIS: the tower — the four legs splayed over the great arch, the
       first and second platforms, the shaft, the top and its mast. */
    eiffel: { w: 5.5, draw: function (g) {
      lmSolid(g, 'M-5.4 4.5Q-3.2 0.2 -1.9 -4Q-1 -8 -0.6 -11.2H0.6Q1 -8 1.9 -4Q3.2 0.2 5.4 4.5H3.4Q0 0.2 -3.4 4.5Z');
      lmLine(g, 'M-2.7 0.4L1.4 -3.8M2.7 0.4L-1.4 -3.8M0 -4.4V-11.2M-1.2 -6.4H1.2M-0.9 -8.8H0.9');
      lmLine(g, 'M-4.4 3.4L-3.3 1.4M4.4 3.4L3.3 1.4');
      lmSolid(g, 'M-4.1 1.4V0.4H4.1V1.4Z');
      lmSolid(g, 'M-2.2 -3.5V-4.4H2.2V-3.5Z');
      lmSolid(g, 'M-1 -11.1V-11.9H1V-11.1Z');
      lmLine(g, 'M0 -11.9V-14');
    } },
    /* VENICE: a gondola, bow to the west like every boat here, the ferro
       on her stem, the seat amidships and the gondolier at the stern with
       his oar in the water. She FLOATS, like the rabelo. */
    gondola: { w: 7.5, noShadow: true, floats: true, draw: function (g) {
      lmLine(g, 'M7.3 -3.8L3.8 4.4');
      lmSolid(g, 'M4.1 3.8L3.5 5.3L3.1 5.1L3.6 3.6Z');
      lmSolid(g, 'M-2.2 2.5V1.3H1.2V2.5Z');
      lmLine(g, 'M5.1 2.5L5.5 0.2L6.1 2.5M5.5 0.2L5.4 -2.2M5.4 -1.6L6.6 -1.2');
      lmCircle(g, 5.3, -2.9, 0.65, 'sv-vig-fill');
      lmCircle(g, 5.3, -2.9, 0.65, 'sv-vig-line');
      lmSolid(g, 'M-8.8 -1.6Q-7.6 2.2 -4.2 2.5H5.2Q7.6 2.3 8.8 0.2Q8.4 3.7 5.2 4H-4.2Q-7.8 3.7 -8.8 -1.6Z');
      lmSolid(g, 'M-8.6 -1.2L-9.6 -1.5L-9.1 -4.6L-8.2 -4.2L-8.5 -1.9Z');
      lmLine(g, 'M-8.8 -2.4H-7.8M-8.7 -3.3H-7.9');
    } },
    /* FLORENCE: Brunelleschi's dome — pointed, ribbed, on its drum with
       the round windows, the lantern on top; the nave under it and
       Giotto's campanile beside. Not the `dome`, which is Sizewell. */
    duomo: { w: 7, draw: function (g) {
      lmSolid(g, 'M5.2 4.5V-7H7.4V4.5Z');
      lmLine(g, 'M4.8 -7H7.8M4.8 -4.6H7.8M5.8 -6.6V-5.2M6.8 -6.6V-5.2');
      lmSolid(g, 'M-7 4.5V1H5.2V4.5Z');
      lmLine(g, 'M-7.4 1H5.4M-5.6 4.5V2.8M-3.4 4.5V2.8M2.8 4.5V2.8');
      lmSolid(g, 'M-3.8 1V-1.6H3.8V1Z');
      lmCircle(g, -2.1, -0.3, 0.45, 'sv-vig-line');
      lmCircle(g, 0, -0.3, 0.45, 'sv-vig-line');
      lmCircle(g, 2.1, -0.3, 0.45, 'sv-vig-line');
      lmLine(g, 'M-4.2 -1.6H4.2');
      lmSolid(g, 'M-3.8 -1.6Q-3.8 -6.4 0 -8.3Q3.8 -6.4 3.8 -1.6Z');
      lmLine(g, 'M-1.9 -1.6Q-1.8 -6 0 -8.3M1.9 -1.6Q1.8 -6 0 -8.3');
      lmSolid(g, 'M-0.75 -8.1V-9.6H0.75V-8.1Z');
      lmSolid(g, 'M-0.9 -9.6L0 -10.9L0.9 -9.6Z');
      lmLine(g, 'M0 -10.9V-12');
    } },
    /* EDINBURGH: the castle on its crag — the rock sheer to the west and
       tailing away east, the walls and the palace along the top, the flag. */
    'castle-rock': { w: 8.5, draw: function (g) {
      lmSolid(g, 'M-4 -1.9V-4.2H3.4V-2.1Z');
      lmLine(g, 'M-4 -4.2V-5H-3.2V-4.2M-2.4 -4.2V-5H-1.6V-4.2M1.6 -4.2V-5H2.4V-4.2');
      lmSolid(g, 'M-1.1 -4.2V-6.8H0.9V-4.2Z');
      lmSolid(g, 'M2.4 -2.1V-3.6L3.9 -5L5.4 -3.6V-1.4Z');
      lmLine(g, 'M-0.1 -6.8V-8.8');
      lmSolid(g, 'M-0.1 -8.8L1.9 -8.2L-0.1 -7.6Z');
      lmSolid(g, 'M-8.6 4.5Q-7.2 1.6 -6.6 -0.6L-5.2 -2L-4.2 -1.6L-3.6 -2.2H3L5.6 -1.3Q7.4 1.4 9 4.5Z');
      lmLine(g, 'M-6.8 1.2L-6.1 3.6M-5.2 -0.8L-4.4 2.2M-3 -1.4L-2.5 0.6M-0.6 -1.6L-0.1 1.4M2.4 -1.6L2.8 0M5.4 -0.4L6.2 2.4');
    } },
    /* MANCHESTER: a cotton mill, six storeys of windows under a flat
       roof and its tank, and the chimney standing over it. */
    'mill-chimney': { w: 7, draw: function (g) {
      lmSolid(g, 'M-7.2 4.5V-2.6H3.2V4.5Z');
      lmLine(g, 'M-7.6 -2.6H3.6');
      var win = '', x, y;
      for (y = -1.6; y < 4; y += 1.3)
        for (x = -6.2; x < 3; x += 1.3) win += 'M' + x.toFixed(1) + ' ' + y.toFixed(1) + 'v0.6';
      lmLine(g, win);
      lmSolid(g, 'M-5.8 -2.6V-3.9H-3.8V-2.6Z');
      lmSolid(g, 'M4.4 4.5L4.9 -10.4H6.3L6.8 4.5Z');
      lmSolid(g, 'M4.6 -10.4V-11.2H6.6V-10.4Z');
      lmLine(g, 'M4.8 -7.6H6.4M4.7 -4.4H6.5');
      lmPath(g, 'M5.8 -11.8q1.4 -1.6 3.2 -1.2q1.6 0.4 2.6 -1', 'sv-lm-smoke');
    } },
    /* CYPRUS: the sea arch at Ayia Napa — a flat bridge of limestone with
       the sea running through under it, its bedding in two lines. (EV2
       first drew Aphrodite's Rock, which is Paphos; Tommy was at Ayia
       Napa.) Not afloat: it is ground. The key stays `sea-stack`. */
    'sea-stack': { w: 7.5, noShadow: true, draw: function (g) {
      lmPath(g, 'M-9.6 3.8q2 -0.7 4 0M5.6 3.8q2 -0.7 4 0M-7.4 5.2q7.4 1.1 14.8 0M-2 3.1q2 -0.6 4 0', 'sv-lm-water');
      lmSolid(g, 'M-7.2 3.6L-6.8 -0.8Q-6.3 -2.6 -4.4 -2.9L3.9 -3.2Q6.3 -3 6.9 -1L7.3 3.6H3.8L3.5 1.9Q2.8 -0.4 0 -0.5Q-2.8 -0.4 -3.5 1.9L-3.8 3.6Z');
      lmLine(g, 'M-6.6 0.4H-4.2M4.1 0.2H6.9M-5.8 -1.8H5.6');
    } },
    /* MALLORCA: a stone tower mill — the tapered tower, the cone cap, and
       the four lattice sails across it. */
    windmill: { w: 6, draw: function (g) {
      lmSolid(g, 'M-2.8 4.5L-1.9 -4H1.9L2.8 4.5Z');
      lmLine(g, 'M-2.5 1.6H2.5M-2.2 -1.2H2.2');
      lmLine(g, 'M-0.6 4.5V3.2a0.6 0.6 0 0 1 1.2 0V4.5');
      lmSolid(g, 'M-2.4 -4L0 -6.6L2.4 -4Z');
      [20, 110, 200, 290].forEach(function (a) {
        g.save();
        g.translate(0, -5);
        g.rotate(a * Math.PI / 180);
        lmLine(g, 'M0 0V-6.6');
        lmSolid(g, 'M0.1 -1.6H1.4V-6.4H0.1Z');
        lmLine(g, 'M0.1 -2.8H1.4M0.1 -4H1.4M0.1 -5.2H1.4');
        g.restore();
      });
      lmCircle(g, 0, -5, 0.45, 'sv-vig-fill');
      lmCircle(g, 0, -5, 0.45, 'sv-vig-line');
    } },
    /* BUDAPEST: the Parliament on the river — the long facade, the steep
       roofs of the two wings, the ribbed dome with its spire, the two
       spires either side of it and the pinnacles along the roof. */
    parliament: { w: 10.5, draw: function (g) {
      lmSolid(g, 'M-10.4 0L-9.8 -1.6H-2.8L-2.2 0ZM10.4 0L9.8 -1.6H2.8L2.2 0Z');
      lmLine(g, 'M-9 -1.6V-2.8M-7.2 -1.6V-2.8M-5.4 -1.6V-2.8M-3.6 -1.6V-2.8M9 -1.6V-2.8M7.2 -1.6V-2.8M5.4 -1.6V-2.8M3.6 -1.6V-2.8');
      lmSolid(g, 'M-10.4 4.5V0H10.4V4.5Z');
      lmLine(g, 'M-10.4 1.8H10.4');
      var arc = '', x;
      for (x = -9.6; x < 9.8; x += 1.2) if (Math.abs(x) > 1.4) arc += 'M' + x.toFixed(1) + ' 4.5V2.8';
      lmLine(g, arc);
      lmSolid(g, 'M-5.2 0V-4H-4V0ZM4 0V-4H5.2V0Z');
      lmSolid(g, 'M-5.3 -4L-4.6 -6.8L-3.9 -4ZM3.9 -4L4.6 -6.8L5.3 -4Z');
      lmSolid(g, 'M-2.6 0V-2.8H2.6V0Z');
      lmSolid(g, 'M-2.9 -2.8Q-2.9 -6.4 0 -7.4Q2.9 -6.4 2.9 -2.8Z');
      lmLine(g, 'M-1.4 -2.8Q-1.3 -6 0 -7.4M1.4 -2.8Q1.3 -6 0 -7.4');
      lmSolid(g, 'M-0.5 -7.2L0 -10.8L0.5 -7.2Z');
      lmSolid(g, 'M-1.1 4.5V1.4a1.1 1.1 0 0 1 2.2 0V4.5Z');
    } },
    /* ISTANBUL: a mosque — the great dome on its drum, the half-domes
       down its sides, and two pencil minarets with their balconies. */
    minaret: { w: 7.5, draw: function (g) {
      [-6.7, 6.7].forEach(function (x) {
        lmSolid(g, 'M' + (x - 0.5) + ' 4.5V-7H' + (x + 0.5) + 'V4.5Z');
        lmSolid(g, 'M' + (x - 0.9) + ' -4.1V-4.6H' + (x + 0.9) + 'V-4.1Z');
        lmSolid(g, 'M' + (x - 0.9) + ' -0.8V-1.3H' + (x + 0.9) + 'V-0.8Z');
        lmSolid(g, 'M' + (x - 0.6) + ' -7L' + x + ' -10.6L' + (x + 0.6) + ' -7Z');
      });
      lmSolid(g, 'M-5.4 4.5V0.6H5.4V4.5Z');
      lmLine(g, 'M-4.2 4.5V2.8a0.6 0.6 0 0 1 1.2 0V4.5M3 4.5V2.8a0.6 0.6 0 0 1 1.2 0V4.5M-0.7 4.5V2.4a0.7 0.7 0 0 1 1.4 0V4.5');
      lmSolid(g, 'M-5.4 0.6a1.8 1.6 0 0 1 3.6 0ZM1.8 0.6a1.8 1.6 0 0 1 3.6 0Z');
      lmSolid(g, 'M-3.6 0.6V-0.7H3.6V0.6Z');
      lmLine(g, 'M-2.4 0.2V-0.3M-1.2 0.2V-0.3M0 0.2V-0.3M1.2 0.2V-0.3M2.4 0.2V-0.3');
      lmSolid(g, 'M-3.4 -0.7a3.4 3.4 0 0 1 6.8 0Z');
      lmLine(g, 'M0 -4.1V-5.6');
    } },
    /* ARUNDEL: a castle — the keep with its battlements and its flag, the
       curtain wall in front with a round-headed gate, a tower at each end. */
    castle: { w: 7, draw: function (g) {
      lmSolid(g, 'M-2.8 0.4V-5.4H2.8V0.4Z');
      lmFill(g, 'M-2.8 -5.4V-6.2H-1.8V-5.4M-0.5 -5.4V-6.2H0.5V-5.4M1.8 -5.4V-6.2H2.8V-5.4Z');
      lmLine(g, 'M-2.8 -5.4V-6.2H-1.8V-5.4M-0.5 -5.4V-6.2H0.5V-5.4M1.8 -5.4V-6.2H2.8V-5.4');
      lmLine(g, 'M-1.3 -4.2V-3M1.3 -4.2V-3');
      lmLine(g, 'M0 -6.2V-9.4');
      lmSolid(g, 'M0 -9.4L3.2 -8.6L0 -7.8Z');
      lmSolid(g, 'M-6.4 4.5V0.2H6.4V4.5Z');
      lmFill(g, 'M-4.4 0.2V-0.6H-3.4V0.2M-2 0.2V-0.6H-1V0.2M1 0.2V-0.6H2V0.2M3.4 0.2V-0.6H4.4V0.2Z');
      lmLine(g, 'M-4.4 0.2V-0.6H-3.4V0.2M-2 0.2V-0.6H-1V0.2M1 0.2V-0.6H2V0.2M3.4 0.2V-0.6H4.4V0.2');
      [-6.4, 6.4].forEach(function (x) {
        lmSolid(g, 'M' + (x - 1.2) + ' 4.5V-1.6H' + (x + 1.2) + 'V4.5Z');
        lmFill(g, 'M' + (x - 1.2) + ' -1.6V-2.3H' + (x - 0.4) + 'V-1.6M' + (x + 0.4) + ' -1.6V-2.3H' + (x + 1.2) + 'V-1.6Z');
        lmLine(g, 'M' + (x - 1.2) + ' -1.6V-2.3H' + (x - 0.4) + 'V-1.6M' + (x + 0.4) + ' -1.6V-2.3H' + (x + 1.2) + 'V-1.6');
      });
      lmFill(g, 'M-1.2 4.5V2a1.2 1.2 0 0 1 2.4 0V4.5Z');
      lmLine(g, 'M-1.2 4.5V2a1.2 1.2 0 0 1 2.4 0V4.5');
    } },
    /* SEVILLE: the Giralda — the tall square shaft with its sebka panels,
       the belfry stage the Christians set on it, the stages above, the
       cupola and the Giraldillo turning on the top. */
    giralda: { w: 4, draw: function (g) {
      lmSolid(g, 'M-2.3 4.5V-4.8H2.3V4.5Z');
      lmLine(g, 'M-1.3 -4.1V-1.3H1.3V-4.1ZM0 -3.9L1.1 -2.7L0 -1.5L-1.1 -2.7ZM0 1.4V2.6M0 -0.4V0.4');
      lmSolid(g, 'M-2 -4.8V-7.3H2V-4.8Z');
      lmLine(g, 'M-2.6 -4.8H2.6M-1 -4.8V-6.3a1 1 0 0 1 2 0V-4.8');
      lmSolid(g, 'M-1.5 -7.3V-9H1.5V-7.3Z');
      lmLine(g, 'M-2.2 -7.3H2.2');
      lmSolid(g, 'M-1 -9V-10.2H1V-9Z');
      lmLine(g, 'M-1.7 -9H1.7');
      lmSolid(g, 'M-0.9 -10.2Q-0.9 -11.7 0 -11.9Q0.9 -11.7 0.9 -10.2Z');
      lmLine(g, 'M0 -11.9V-13.4M-0.6 -12.8H0.6');
    } },

    /* --- landmarks the RULES put there, with no data at all -------- */
    /* A CAIRN ON EVERY SUMMIT that stands above the level of a directed
       day: the walker's mark on a top, and the only thing that has ever
       been built on one of these hills. */
    cairn: { w: 3.6, draw: function (g) {
      lmSolid(g, 'M-3.4 4.5L0 -5L3.4 4.5Z');
      lmLine(g, 'M-2.4 1.7H2.4M-1.4 -1.1H1.4M-0.7 4.5V1.7M0.9 1.7V-1.1');
      lmLine(g, 'M0 -5V-6.4');
    } },
    /* A WRECK AT THE DEEPEST SOUNDING of every named deep. GROUND
       FURNITURE, not a standing thing (v23, C). Tommy, on the tilted sheet:
       "it's still just a picture of a boat that's on the sea bed." It was
       — standing upright on the glass like lettering. A wreck lies ON the
       bed: it goes into the draped texture (`bed`, kept out of FLAT_SEL)
       and is seen through the water, and it is drawn the way a chart draws
       one, IN PLAN — a hull outline from above with the ribs showing
       through, some of them broken — and laid a little off the fall line
       of the bed, since nothing sinks square to the slope. */
    wreck: { w: 7, noShadow: true, bed: true, draw: function (g) {
      // the hull, bow to the right, seen from above
      lmLine(g, HULL_D);
      lmLine(g, HULL_KEEL);                                      // the keel
      lmLine(g, HULL_RIBS);                                      // the ribs
      lmLine(g, 'M-4.2 1.9V2.2M1 1.4V2.4');                     // and the broken ones
      lmLine(g, 'M-7.6 -0.6L-8.8 -1.4M-7.6 0.6L-8.8 1.4');       // the stern post, split
    } },
    /* ==== BT2 (2026-09-26): THE WRECK AT 2022 IS THE STEAMSHIP, BROKEN.
       Tommy, of the wreck in plan this mark used to stand (`wreck` above,
       which stays: it is the sheet's own mark at every named deep): it
       read as a fish with a boat round it. This is the ship JDLT was — the
       same `steamer` she sails on from here to the Shipyard and stands on
       the stocks there — broken-backed on the rocks and awash: the fore
       part reared up out of the water with its mast thrown back, the after
       part settled with her funnel leaning and the mainmast snapped, the
       davits empty (her lifeboat is the boat that brought you here), the
       rest of her hull under the surface and seen through it in the sea's
       own ink. Stood on the water like the other marks; the waterline is
       WL. */
    'steamer-wreck': { w: 12, noShadow: true, draw: function (g) {
      var S = 0.86, WL = 3.1;
      var BOW_CUT = 'M-30 -30H-4.6L-3.8 -6L-4.8 -3L-3.6 -0.2L-4.6 2.2L-3.9 4.6L-4.6 30H-30Z';
      var AFT_CUT = 'M30 -30H-4.3L-3.5 -6L-4.5 -3L-3.3 -0.2L-4.3 2.2L-3.6 4.6L-4.3 30H30Z';
      var JAG_B = 'M-4.6 -1L-3.6 -0.2L-4.6 2.2L-3.9 4.6', JAG_A = 'M-4.3 -1L-3.3 -0.2L-4.3 2.2L-3.6 4.6';
      function piece(px, py, rot, cut, jag, above, o) {
        g.save();
        g.beginPath();
        if (above) g.rect(-16, -16, 32, 16 + WL); else g.rect(-16, WL, 32, 12);
        g.clip();
        g.translate(px, py); g.rotate(rot); g.scale(S, S); g.translate(4.2, -4.3);
        g.clip(P(cut));
        if (above) {
          steamer(g, o);
          g.save(); g.clip(P(STEAMER_HULL)); lmPath(g, jag, 'sv-ship-line'); g.restore();
        } else {
          lmPath(g, STEAMER_HULL + 'M-11.72 3.1H11.9', 'sv-lm-water');
        }
        g.restore();
      }
      // under the water first: what is left of her hull, seen through it
      piece(-2.6, 5.3, 0.52, BOW_CUT, JAG_B, false, {});
      piece(-1.3, 3.8, 0.1, AFT_CUT, JAG_A, false, {});
      // then above it: the after part settled, the fore part reared up
      piece(-1.3, 3.8, 0.1, AFT_CUT, JAG_A, true, { boat: false, stump: true });
      piece(-2.6, 5.3, 0.52, BOW_CUT, JAG_B, true, {});
      // the sea over her: the surface, broken water at both parts, and a spar adrift
      lmPath(g, 'M-15 3.1q1.5 -0.55 3 0t3 0t3 0t3 0t3 0t3 0t3 0t3 0t3 0t3 0', 'sv-lm-water');
      lmPath(g, 'M-8.6 3.8q2.2 0.8 4.4 0M-2 3.7q1.8 0.6 3.6 0M4 3.8q3 0.8 6 0M-12 5q4 1 8 0M3 5.1q4 1 8 0', 'sv-lm-water');
      lmPath(g, 'M-7.6 2.7l0.3 -0.5M-6.9 2.6l0.1 -0.6M-3.4 2.6l0.3 -0.5M10.6 2.7l0.3 -0.5', 'sv-lm-water');
      lmPath(g, 'M-13.6 4.4l2.8 -0.35', 'sv-ship-line');
    } },
    /* THE SHIP, on the open water of the sheet in hand (the ornament that
       has sailed the sheet since v6): hull, three masts, courses and
       topsails, a pennant at the main. She floats (v23, C). */
    ship: { w: 7, noShadow: true, floats: true, draw: function (g) {
      lmPath(g, 'M-6.3 2.5q1.7 2.1 6.3 2.1t6.3 -2.1q-2.5 0.8 -6.3 0.8t-6.3 -0.8z', 'sv-ship-hull');
      lmPath(g, 'M-6.3 2.5H6.3M-4.6 2.5V-3.8M0 2.5V-6.7M4.6 2.5V-2.9M-0.8 -6.7q-1.3 -1.3 0 -2.1', 'sv-ship-line');
      lmPath(g, 'M-6.3 -1.7q1.7 -1.3 3.4 0v2.9q-1.7 -1.3 -3.4 0z', 'sv-ship-sail');
      lmPath(g, 'M-1.7 -5.5q1.7 -1.3 3.4 0v3.8q-1.7 -1.3 -3.4 0z', 'sv-ship-sail');
      lmPath(g, 'M2.9 -1.3q1.7 -1.3 3.4 0v2.5q-1.7 -1.3 -3.4 0z', 'sv-ship-sail');
      lmPath(g, 'M0 -6.7l3.8 0.8-3.8 0.8z', 'sv-ship-flag');
    } },
    /* ONE WHALE IN THE PRECAMBRIAN SEA, spouting. Where she is is hashed
       from the date, so she has moved by the morning. She floats. */
    whale: { w: 8, noShadow: true, floats: true, draw: function (g) {
      lmSolid(g, 'M-6.6 3.4q3.4 -5.2 9.6 -3.6q3.6 0.9 4.6 3.6Z');
      lmLine(g, 'M-6.6 3.4q-2.8 -1 -4.4 -3.2q1.6 3 0.4 4.6q2.4 -1 4 -1.4');
      lmLine(g, 'M4.4 -0.4a0.5 0.5 0 1 1 0.1 0.1');
      lmPath(g, 'M6.2 -1.6q-1.4 -3.4 -2.6 -4.6M6.4 -1.8q0.8 -3.4 2.8 -4.4', 'sv-lm-smoke');
    } },
    /* ==== X5: THE FIVE MARKS OF THE VOYAGE =========================
       West of January 2022 the record stops and the sea runs on without
       end. These are what is IN it — drawn in exactly the hand the rest
       of this table is drawn in, in map units about their own point of
       ground, the ground line at y = 4.5, pencil and one wash. Nothing
       here is a game sprite: a harbour is a mole and a light, a buoy is a
       can with a topmark, a shoal is the chart's own dotted danger, and
       the boat is a boat. They are only ever cut when `?voyage=1`.
       ================================================================ */

    /* A HARBOUR: a mole running out into the water with a light on its
       head, and the still water it makes behind it. The record's own
       shore has one (the voyage casts off from it) and so has the
       founding of 2017, which is a mole that was never finished. */
    harbour: { w: 9.5, draw: function (g) {
      // the still water inside the arm
      lmPath(g, 'M-9.4 5.6q5 1.6 10 0M-8 7q4 1.2 8 0', 'sv-lm-water');
      // the mole itself, low, with its coping and its courses
      lmSolid(g, 'M-9.4 4.5V1.7H6.6V4.5Z');
      lmLine(g, 'M-9.4 1.7H6.6');
      lmLine(g, 'M-6.8 1.7V4.5M-4.2 1.7V4.5M-1.6 1.7V4.5M1 1.7V4.5M3.6 1.7V4.5');
      // the light on its head: a short tower, a lantern, and the ray
      lmSolid(g, 'M4.6 1.7L5 -2.9H7L7.4 1.7Z');
      lmSolid(g, 'M4.4 -2.9H7.6V-4.5H4.4Z');
      lmLine(g, 'M6 -4.5V-5.7M4.9 -5.1H7.1');
      lmPath(g, 'M8.6 -3.9L11.8 -4.9M8.6 -3.3L11.8 -2.6', 'sv-lm-ray');
    } },

    /* A BUOY, which is the one mark in the sea that is not fixed to
       anything: a can, a band, a staff and a ball topmark. She floats. */
    buoy: { w: 3.2, noShadow: true, floats: true, draw: function (g) {
      lmSolid(g, 'M-2 4.2V0.4L-1.3 -1.6H1.3L2 0.4V4.2Z');
      lmLine(g, 'M-1.9 1.9H1.9');
      lmLine(g, 'M0 -1.6V-6');
      lmSolid(g, 'M-1.1 -6a1.1 1.1 0 1 1 2.2 0a1.1 1.1 0 1 1 -2.2 0Z');
    } },

    /* A SHOAL, as a chart draws a danger and not as a picture: the
       dotted limit of the shallow water with the stipple of the bottom
       inside it. GROUND FURNITURE like the wreck — it lies ON the bed
       and is seen through the water, so it is faint until you are over
       it, which is the point of carrying a lead line. */
    shoal: { w: 9, noShadow: true, bed: true, draw: function (g) {
      var d = '', i, a, N = 26, RX = 9, RY = 5.4;
      for (i = 0; i < N; i++) {
        a = i / N * Math.PI * 2;
        var b = a + Math.PI / N * 0.85;
        d += 'M' + (Math.cos(a) * RX).toFixed(2) + ' ' + (Math.sin(a) * RY).toFixed(2) +
             'L' + (Math.cos(b) * RX).toFixed(2) + ' ' + (Math.sin(b) * RY).toFixed(2);
      }
      lmPath(g, d, 'sv-lm-water');
      // the bottom showing through: the chart's own stipple, on a rule
      var st = '';
      [[-5.4, -1.6], [-3.2, 1.4], [-1.2, -2.2], [0.6, 0.9], [2.6, -1.1],
       [4.6, 1.7], [-4.4, 2.2], [1.8, 2.6], [3.4, -2.6], [-2.2, -0.2]].forEach(function (q) {
        st += 'M' + q[0] + ' ' + q[1] + 'l0.01 0';
      });
      lmPath(g, st, 'sv-vig-line');
    } },

    /* ==== BT2 (2026-09-26): THE BOATS BY LIFE PHASE. The lugger is
       retired. Tommy: "you have the boat, and then you lose the boat
       because you're going backwards, but it's quite fun because you get a
       new boat! You don't understand what it is, and then when you get to
       the destination, it tells you." Sailing west is sailing back through
       a life, so each leg between two marks is sailed in the boat of that
       part of it, and the card of the mark at the leg's west end says
       which boat it was. Going west, and smaller as she goes back:
         harbour -> Wreck        the steamship's LIFEBOAT (what got off her)
         Wreck -> Shipyard       the STEAMSHIP, whole (JDLT: twelve aboard)
         Shipyard -> Cable       a CABLE SHIP, paying cable out astern
         Cable -> Two Rocks      a small gaff CUTTER, sailed single-handed
         Two Rocks -> Slipway    a MIRROR dinghy, red sails (the learner)
         Slipway -> Code Point   a RAFT, lashed from what was lying around
         Code Point -> Hundred   a PAPER BOAT, folded from a ruled page
         Hundred -> the berth    a BASKET (round, a coracle; VY2: Tommy,
                                 "go with default round one for basket")
       Every one is side on with her bow to the WEST (left), the way the
       lugger was drawn and the way the passage runs; waterline and keel at
       y 4.3, so the ripple (`afloat`) is under her as it was under the
       lugger. Sizes are in art units at the one BOAT_K: the two steamers
       26 long, the lifeboat 11, the cutter 12 and her bowsprit, and the
       little ones 9 to 10 — never under 9, which at BOAT_K is 22 css px. */

    /* THE LIFEBOAT: double-ended and open, clinker strakes, the grab line
       looped along her side, three oars out and a steering oar astern. */
    lifeboat: { w: 6, noShadow: true, floats: true, draw: function (g) {
      // drawn 11 long and stood up 1.15x about her waterline, so she is not
      // the smallest thing afloat on the first leg
      g.translate(0, 4.3); g.scale(1.15, 1.15); g.translate(0, -4.3);
      // the far oars, behind her, only their blades showing under the keel
      lmPath(g, 'M-2.2 2.2L-3.6 5.2M0.6 2.2L-0.8 5.2M3.2 2.2L1.8 5.2', 'sv-ship-line');
      lmPath(g, 'M-5.7 1.2Q-5 4.3 0 4.3Q5 4.3 5.7 1.2Q0 2.5 -5.7 1.2Z', 'sv-ship-hull');
      lmPath(g, 'M-5.7 1.2l-0.3 -0.7M5.7 1.2l0.3 -0.7', 'sv-ship-line');          // stem and stern posts
      lmPath(g, 'M-5.2 2.5Q0 3.5 5.2 2.5M-4.5 3.4Q0 4.05 4.5 3.4', 'sv-vig-line');   // the strakes
      // the grab line, festooned in loops under the gunwale
      lmPath(g, 'M-4.4 1.9q0.55 0.7 1.1 0.1t1.1 0.2t1.1 0.1t1.1 0t1.1 0t1.1 -0.1t1.1 -0.2t1.1 -0.1', 'sv-ship-line');
      // the near oars, over her side and down into the water, blades and all
      lmPath(g, 'M-1.8 1.85L-3.9 5.3M1 1.95L-1.1 5.4M3.6 1.8L1.5 5.3', 'sv-ship-line');
      lmPath(g, 'M-3.6 4.8l-0.7 1.1 0.35 0.2 0.7 -1.1zM-0.8 4.9l-0.7 1.1 0.35 0.2 0.7 -1.1zM1.8 4.8l-0.7 1.1 0.35 0.2 0.7 -1.1z', 'sv-ship-ink');
      // the steering oar over her stern
      lmPath(g, 'M5.4 1.1L8 4.6', 'sv-ship-line');
      lmPath(g, 'M7.6 4l0.9 1.3 0.4 -0.3 -0.9 -1.3z', 'sv-ship-ink');
    } },

    /* THE STEAMSHIP, whole, under way: smoke, her boat in its davits, the
       ensign. The figure is `steamer` above, shared with the yard and the
       wreck. */
    steamship: { w: 11, noShadow: true, floats: true, draw: function (g) {
      steamer(g, { smoke: true, boat: true, flag: true });
    } },

    /* THE CABLE SHIP. What makes her one is at her two ends: the bow
       sheaves, big wheels on a beam over her stem, and the stern sheave
       the cable runs out over, down into the water astern. A clipper bow,
       one tall funnel, two masts, the cable drum on her after deck. */
    'cable-ship': { w: 11, noShadow: true, floats: true, draw: function (g) {
      lmPath(g, 'M-7.4 -9.8L-12.2 0.2M10.4 -8.4L12.9 1M-7.4 -9.6L10.4 -8.2', 'sv-ship-line');
      lmPath(g, 'M-7.8 1L-7.4 -9.8M-8.2 -7.8H-6.7M10 1.4L10.4 -8.4M9.6 -6.8H11.1', 'sv-ship-line');
      // the funnel, tall and upright, black-topped; its smoke
      lmPath(g, 'M3.1 1.6L3.4 -7H5.4L5.2 1.6Z', 'sv-ship-hull');
      lmPath(g, 'M3.36 -6.1L3.4 -7H5.4L5.35 -6.1Z', 'sv-ship-ink');
      lmPath(g, 'M4.4 -7.4q1.2 -1.4 3 -1.1t3.2 -1.6q1.4 -0.7 2.8 -0.2', 'sv-lm-smoke');
      // the house, the bridge
      lmPath(g, 'M-4 1.8V-0.6H2.4V1.8Z', 'sv-ship-hull');
      lmPath(g, 'M-3.4 -0.6V-2.5H0.3V-0.6Z', 'sv-ship-hull');
      lmPath(g, 'M-4.2 -2.5H0.7M-2.8 -2V-1.2M-2 -2V-1.2M-1.2 -2V-1.2M-0.4 -2V-1.2' +
                'M-3.2 0.4h0.7M-1.8 0.4h0.7M-0.4 0.4h0.7M1 0.4h0.7', 'sv-ship-line');
      // the cable drum on the after deck, and the cable off it over the stern
      lmPath(g, 'M7.9 0.4m-1.3 0a1.3 1.3 0 1 0 2.6 0a1.3 1.3 0 1 0 -2.6 0Z', 'sv-ship-hull');
      lmCircle(g, 7.9, 0.4, 0.55, 'sv-ship-line');
      lmPath(g, 'M8 -0.9Q11 -1 13.4 0.1', 'sv-ship-line');
      // the hull, with a clipper bow
      lmPath(g, 'M-12.2 0.2Q-11.9 2.4 -10.6 4.3H10.4Q12.3 4.2 13 1Q0.5 2.9 -12.2 0.2Z', 'sv-ship-hull');
      var h = '', x;
      for (x = -10.2; x < 10.2; x += 0.8) h += 'M' + x.toFixed(1) + ' 3.3V4.2';
      lmPath(g, h, 'sv-lm-ray');
      lmPath(g, 'M-11.25 3.2H12.05', 'sv-ship-line');
      for (x = -8.8; x <= 9.5; x += 2.3) lmCircle(g, x, 2.2, 0.3, 'sv-ship-line');
      // THE BOW SHEAVES: the beam out over her stem and two wheels on it
      lmPath(g, 'M-11.8 -0.3L-14.6 -0.9V0.1L-11.9 0.9Z', 'sv-ship-hull');
      lmPath(g, 'M-14 0.6m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0Z', 'sv-ship-hull');
      lmPath(g, 'M-12.6 0.8m-0.8 0a0.8 0.8 0 1 0 1.6 0a0.8 0.8 0 1 0 -1.6 0Z', 'sv-ship-hull');
      lmPath(g, 'M-14 -0.3V1.5M-14.9 0.6H-13.1M-12.6 0.1V1.5M-13.3 0.8H-11.9', 'sv-ship-line');
      // THE STERN SHEAVE, and the cable going down off it into the sea
      lmPath(g, 'M13.4 0.9m-0.9 0a0.9 0.9 0 1 0 1.8 0a0.9 0.9 0 1 0 -1.8 0Z', 'sv-ship-hull');
      lmPath(g, 'M13.4 0.1V1.7M12.6 0.9H14.2', 'sv-ship-line');
      lmPath(g, 'M14.3 0.9Q14.8 3.2 14.2 5.6', 'sv-ship-line');
    } },

    /* THE CUTTER: a small gaff cutter, one mast, one pair of hands. A
       straight stem and a bowsprit, the gaff main on its boom, a jib out to
       the bowsprit's end, a pennant at the truck. */
    cutter: { w: 6, noShadow: true, floats: true, draw: function (g) {
      lmPath(g, 'M-1.4 -7.6L4.8 -10.2L7.1 0.4L-1.4 0.6Z', 'sv-ship-sail');      // the gaff main
      lmPath(g, 'M0.6 -8.3L1.9 0.5M2.7 -9.2L4.4 0.45', 'sv-lm-ray');             // its seams
      lmPath(g, 'M-2 -8.4L-9.2 0.8L-2.5 0.7Z', 'sv-ship-sail');                  // the jib
      lmPath(g, 'M-6 1.4L-5.2 4.3H4.2Q5.8 4.2 6.2 1.8Q0 2.3 -6 1.4Z', 'sv-ship-hull');
      lmPath(g, 'M-1.6 2L-1.6 -10.2M-1.6 -7.4L5 -10.5M-1.6 0.6L7.6 0.35M-5.9 1.3L-9.6 0.6' +
                'M-1.6 -10.2L-9.4 0.7M5.6 1.9L7.4 1', 'sv-ship-line');           // mast, gaff, boom, sprit, stay, tiller
      lmPath(g, 'M-5.4 2.9Q0 3.5 5.6 3.1', 'sv-vig-line');                       // her sheer strake
      lmPath(g, 'M-1.6 -10.2l2.4 0.5 -2.4 0.5z', 'sv-ship-flag');
    } },

    /* THE MIRROR DINGHY: the red-sailed kit boat a British child learns
       in — a pram, so her bow is a small square transom and not a point;
       a gunter main, its yard carrying the red sail up past the mast, and
       a red jib. */
    'mirror-dinghy': { w: 4.6, noShadow: true, floats: true, draw: function (g) {
      lmPath(g, 'M-1.9 1.1V-4.4L-1.2 -9.2L4.4 0.9Z', 'sv-ship-red');             // the gunter main
      lmPath(g, 'M-2.3 -5.4L-4.3 1.2L-2.5 1.15Z', 'sv-ship-red');                // the jib
      lmPath(g, 'M-4.6 1.5L-4.2 3.1Q-3.4 4.3 -1.2 4.3H3.4Q4.5 4.2 4.8 1.8Q0 2.4 -4.6 1.5Z', 'sv-ship-hull');
      lmPath(g, 'M-4.3 2.75Q0 3.3 4.6 2.9', 'sv-vig-line');                      // the chine
      lmPath(g, 'M-1.9 1.9V-4.4L-1.2 -9.2M-1.9 1.1H4.8M-2.1 -5.2L-4.5 1.4M4.6 1.8L6.2 0.9L7 1.2', 'sv-ship-line');
    } },

    /* THE RAFT, lashed together from what was lying around: planks on two
       oil drums, the lashings crossed over them, a bent pole for a mast, a
       scrap of sail on a stick with a patch sewn in it and a ragged foot,
       and a red rag tied at the top. */
    raft: { w: 5, noShadow: true, floats: true, draw: function (g) {
      lmPath(g, 'M-0.3 2.2L-0.05 -2.4L0.2 -6.9', 'sv-ship-line');                // the pole
      lmPath(g, 'M-2.7 -6.2L2.9 -6.7L3.3 -0.7L2 -1.2L0.7 -0.4L-0.7 -1.1L-2.4 -0.6Z', 'sv-ship-sail');
      lmPath(g, 'M0.5 -4.5h1.5v1.4h-1.5z', 'sv-vig-line');                        // the patch
      lmPath(g, 'M0.3 -4.7h1.9v1.8h-1.9z', 'sv-lm-ray');                         // its stitches
      lmPath(g, 'M-2.9 -6.15L3.1 -6.75M3.3 -0.7L4.5 2', 'sv-ship-line');         // the stick, the sheet
      lmPath(g, 'M0.2 -6.9l1.8 0.25 -1.4 0.9z', 'sv-ship-flag');                 // the rag
      // the two drums, their hoops
      lmPath(g, 'M-4.4 3.2h3.2v1.5h-3.2zM1.1 3.2h3.2v1.5h-3.2z', 'sv-ship-hull');
      lmPath(g, 'M-3.6 3.2v1.5M-2 3.2v1.5M1.9 3.2v1.5M3.5 3.2v1.5', 'sv-vig-line');
      // the planks: odd lengths, one proud of the rest
      lmPath(g, 'M-5.3 2.3L4.4 2L5.5 2.5L5.3 3.25L-5.1 3.35Z', 'sv-ship-hull');
      lmPath(g, 'M-5.2 2.8H4.9M-2.4 2.2V3.3M1.6 2.1V3.3', 'sv-vig-line');
      // the lashings, crossed over plank and drum
      lmPath(g, 'M-4 2.3L-3.2 4.6M-3.2 2.3L-4 4.6M2.9 2.1L3.7 4.6M3.7 2.1L2.9 4.6', 'sv-ship-line');
    } },

    /* THE PAPER BOAT, folded out of a page of a ruled exercise book: the
       two flaps and the peak between them, the creases, the blue rules
       and the red margin showing through. */
    'paper-boat': { w: 4.4, noShadow: true, floats: true, draw: function (g) {
      var PEAK = 'M-2.7 1.7L0 -5L2.7 1.7Z';
      var HULL = 'M-4.9 0.4L-2.9 4.3H2.9L4.9 0.4L2.7 1.7H-2.7Z';
      lmPath(g, PEAK, 'sv-ship-sail');
      lmPath(g, HULL, 'sv-ship-hull');
      g.save(); g.clip(P(PEAK)); lmPath(g, 'M-3 -1.9H3M-3 -0.3H3M-3 1.3H3', 'sv-lm-water'); g.restore();
      g.save(); g.clip(P(HULL)); lmPath(g, 'M-5 1.7H5M-5 3.2H5', 'sv-lm-water'); lmPath(g, 'M-3.4 0V4.4', 'sv-lm-margin'); g.restore();
      lmPath(g, PEAK, 'sv-ship-line');
      lmPath(g, HULL, 'sv-ship-line');
      lmPath(g, 'M0 -5V1.7M-2.7 1.7L-1.6 4.3M2.7 1.7L1.6 4.3', 'sv-vig-line');    // the creases
    } },

    /* ==== VY2 (2026-09-26): THE BASKET, REDRAWN TO READ AT A GLANCE.
       Tommy, of BT2's coracle at sailing size: "quite unobvious as a boat
       icon, i have no idea what i'm looking at unfortunately". BT2's was a
       fine drawing that vanished when small: a pale bowl, eleven stakes
       and the weave in hairline hatching, and a blanket. So, what reads at
       22 css px: a round wicker boat AFLOAT, side on and seen a little from
       above so the rim is an open ellipse; the bowl in straw with a few
       BOLD weave strokes (seven stakes, two weaver bands, the over-and-
       under in short heavy ticks) and a thick bound rim; a waterline across
       her with a little bow wave ahead (west, the way she goes) and a wake
       astern; and the one thing that says VESSEL — a single paddle resting
       in her, its grip up over the bow and its blade trailing in the water
       astern. The same scale as the raft and the paper boat. ==== */
    basket: { w: 4.6, noShadow: true, floats: true, draw: function (g) {
      var RX = 4.6, RY = 1.4, CY = 0.3, WL = 4.3, i, j, d;
      // the inside: the far wall, in shade
      lmEllipse(g, 0, CY, RX, RY, 'sv-ship-wicker');
      lmPath(g, 'M-3.7 0.2q1.6 0.9 3.6 1M-2.6 -0.45q1.4 0.8 3.3 0.9M0.8 -0.9q1.3 0.6 2.8 0.9', 'sv-vig-line');
      // the bowl's near face, straw, down to the water
      var FACE = 'M-4.6 0.3C-4.6 2.6 -3.4 4.3 -1.9 4.3H1.9C3.4 4.3 4.6 2.6 4.6 0.3A4.6 1.4 0 0 1 -4.6 0.3Z';
      lmPath(g, FACE, 'sv-ship-wicker');
      function near(x) { var t = Math.max(0, 1 - (x / RX) * (x / RX)); return CY + RY * Math.sqrt(t); }
      function at(u, v) {                // u 0..1 across the near face, v 0..1 rim to water
        var x0 = -RX + 2 * RX * u, y0 = near(x0);
        var w = 1 - v * v * 0.55;          // the bowl draws in toward the water
        return [x0 * w, y0 + (WL - y0) * v];
      }
      // seven stakes, rim to water
      d = '';
      for (i = 1; i <= 7; i++) {
        d += 'M';
        for (j = 0; j <= 5; j++) { var q = at(i / 8, j / 5); d += (j ? 'L' : '') + q[0].toFixed(2) + ' ' + q[1].toFixed(2); }
      }
      lmPath(g, d, 'sv-vig-line');
      // two weaver bands
      d = '';
      [0.36, 0.7].forEach(function (v) {
        d += 'M';
        for (i = 0; i <= 14; i++) { var q = at(0.03 + 0.94 * i / 14, v); d += (i ? 'L' : '') + q[0].toFixed(2) + ' ' + q[1].toFixed(2); }
      });
      lmPath(g, d, 'sv-ship-line');
      // over and under: a heavy tick in every other cell
      d = '';
      for (i = 0; i < 8; i++) for (j = 0; j < 3; j++) {
        if ((i + j) % 2) continue;
        var vv = [0.18, 0.53, 0.86][j], a0 = at((i + 0.28) / 8, vv), a1 = at((i + 0.72) / 8, vv);
        if (a0[0] < -RX + 0.3 || a1[0] > RX - 0.3) continue;
        d += 'M' + a0[0].toFixed(2) + ' ' + a0[1].toFixed(2) + 'L' + a1[0].toFixed(2) + ' ' + a1[1].toFixed(2);
      }
      lmPath(g, d, 'sv-ship-line');
      lmPath(g, FACE, 'sv-ship-line');
      // the rim: a thick bound rod all round, the binding on the slant
      lmPath(g, 'M-4.6 0.3A4.6 1.4 0 0 0 4.6 0.3A4.6 1.4 0 0 0 -4.6 0.3Z', 'sv-ship-line');
      lmPath(g, 'M-4.8 0.3A4.8 1.62 0 0 0 4.8 0.3', 'sv-ship-line');
      rim(g, RX + 0.1, RY + 0.12, CY, 14);
      // THE PADDLE, resting in her: the grip up over the bow, the loom
      // across the rim, the blade trailing in the water astern
      lmPath(g, 'M-2.9 -4.3L5.4 3.2', 'sv-ship-line');
      lmPath(g, 'M-3.7 -3.6L-2.1 -5', 'sv-ship-line');                                  // the grip
      lmPath(g, 'M4.7 2.4Q6.3 2.9 7.6 4.6Q6.9 5.2 6 4.7Q5 3.9 4.7 2.4Z', 'sv-ship-hull');  // the blade
      // the waterline across her, a bow wave ahead and a wake astern
      lmPath(g, 'M-2.8 4.45H2.6M-6.4 4.4q0.7 -0.9 1.7 -0.7M-7 4.9q1.1 -0.4 2.2 -0.2' +
                'M5 4.95q1.5 0.25 3.1 -0.05M5.4 5.6q1.1 0.15 2.2 -0.05', 'sv-lm-water');
    } },
    /* ==== VY2 (2026-09-26): THE REEDS ARE GONE. BT2's bed of reeds and
       bulrushes at 12 April 1986 (`reeds`, `reeds-front`) is deleted:
       Tommy, "i wanted whatever boat we have to 'disappear' off the map
       into something as i don't 'park' at birth, i stop existing". She
       sails in between the legs and goes out as a dot (voyage.js). ==== */

    /* A MILESTONE AT EVERY 1 JANUARY, set where the meridian meets the
       shore: the year boundary, in the form the road takes it. */
    milestone: { w: 2.6, draw: function (g) {
      lmSolid(g, 'M-2.2 4.5V-1.8a2.2 2.2 0 0 1 4.4 0V4.5Z');
      lmLine(g, 'M-1.3 -1.2H1.3M-1.3 0.5H1.3M-1.3 2.2H1.3');
      lmLine(g, 'M-4.6 5.2q4.6 1 9.2 0');
    } },

    /* ==== V3: `loom` AND `lantern-lit` WENT WITH THE LIGHT AT 1986.
       X11 stood a lighthouse on the water at 12 April 1986, lit at night,
       with its loom laid on the sea toward the eye. V3 puts a QUAY there
       instead — Tommy's *"a fun play for my date of birth between birth
       and berth"* — and a quay carries no light, so both drawings and the
       `sv-lm-loom` ink they were the only users of are gone. The lamp
       that burns in the 2017 pier, the 1994 window and the 2007 gallery
       is `lamp-lit` below and is untouched. ==== */

    /* ==== V2: THE SEVEN MARKS OF THE VOYAGE, WHICH ARE HIS ============
       EGGS.md section 4. The sea west of January 2022 carries seven marks
       and they are the dates of a life, not of an industry: the wreck he
       left in January 2022, the yard he built her in, the cable he came
       to London on, the rocks of two systems nobody sailed, the slip he
       was launched down, the signal station where the code starts, and
       the rock in the deepest water with the one sounding beside it.
       Three of the shapes are LOCKED and four are OPEN and built as
       variants behind `?marks=a|b|c` for Tommy to pick; the default is
       (a) everywhere and he has NOT picked.

       They are drawn in this table's own vocabulary and in nothing else:
       map units about their own point of ground, the ground line at
       y = 4.5, the paper showing through the ink, `sv-lm-water` for
       anything the sea does, `sv-lm-ray` for a light, the ship inks for
       anything with a hull. No image is loaded and no cell is bigger than
       the box the six before them are cut in. ================== */

    /* 2017 — THE YARD, and the pun is the trade: JDLT shipped code, and a
       yard ships hulls. (Variant a of an OPEN mark.) BT2 (2026-09-26):
       what stands on the slip is THE STEAMSHIP, whole — the same figure
       (`steamer`) she sails from the Wreck to here and lies broken as at
       the Wreck. Tommy: "any changes we make to the shipwreck would need
       to change the 2018 launch of that same boat." She sits on the keel
       blocks of an inclined way, stern to the water the way a hull is
       launched, shored up along her side, with staging at her two ends
       and the yard's sheerlegs crane over her. No smoke: she has not been
       lit. */
    shipyard: { w: 12, draw: function (g) {
      var S = 0.62, TX = -3, TY = -1.07, R = 0.07;
      lmPath(g, 'M5.4 5.3q4.6 1.4 9.4 0M8 6.8q3.3 1 6.6 0', 'sv-lm-water');
      // the sheerlegs crane, behind everything, its jib over her middle
      lmLine(g, 'M-14.4 2.2L-13.2 -9.6L-12 2.2M-13.9 -2.6H-12.5M-13.6 -6.1H-12.8');
      lmLine(g, 'M-13.2 -9.6L-1.8 -8.8M-13.2 -9.6L-15 2.2M-1.8 -8.8V-7.3');
      lmLine(g, 'M-1.8 -7.3q0.55 0.5 0 0.95');
      // the ground and the way running down it into the water
      lmSolid(g, 'M-15 4.5V2.2H-12.4L6.6 3.55V4.5Z');
      lmLine(g, 'M6.6 3.55L13.6 4.05');
      // her keel line on the way, in sheet units, for the blocks and shores
      function keelY(x) { return TY + 4.3 * S / Math.cos(R) + (x - TX) * Math.tan(R); }
      function wayY(x) { return 2.2 + (x + 12.4) * 0.0711; }
      var blk = '', shr = '', x;
      for (x = -9; x <= 3.5; x += 2.5) {
        var k = keelY(x), w = wayY(x);
        blk += 'M' + (x - 0.35).toFixed(2) + ' ' + w.toFixed(2) + 'V' + k.toFixed(2) + 'H' + (x + 0.35).toFixed(2) + 'V' + w.toFixed(2) + 'Z';
      }
      [-8.2, -4.4, -0.6, 2.8].forEach(function (x) {
        shr += 'M' + (x - 1.6).toFixed(2) + ' ' + wayY(x - 1.6).toFixed(2) + 'L' + x.toFixed(2) + ' ' + (keelY(x) - 1.3).toFixed(2);
      });
      lmSolid(g, blk);
      // the staging at her bow and her stern: poles and two ledgers
      lmLine(g, 'M-12.1 2.2V-1.4M-10.9 2.15V-1.4M-12.5 0.4H-10.5M-12.5 -1H-10.5');
      lmLine(g, 'M5.2 3.3V0M6.3 3.45V0M4.8 1.6H6.7M4.8 0.3H6.7');
      g.save(); g.translate(TX, TY); g.rotate(R); g.scale(S, S);
      steamer(g, { smoke: false, boat: true, flag: false });
      g.restore();
      lmLine(g, shr);                                             // the shores, in front of her side
    } },

    /* 2017 variant b — A PIER WITH A LIGHT AT ITS HEAD. Tommy: *"If you
       want something built, why not something like a pier?"* Piles, a
       deck, and the light standing at the head of it. */
    'pier-light': { w: 12, draw: function (g) {
      lmPath(g, 'M-4 6.4q8 2.2 16 0M-1 7.8q6 1.8 12 0', 'sv-lm-water');
      lmSolid(g, 'M-15 4.5V2.8H-6.6V4.5Z');                     // the bank it runs off
      lmSolid(g, 'M-7.6 1.1H8V2.4H-7.6Z');                      // the deck
      lmLine(g, 'M-5.4 2.4V5M-2.6 2.4V5.3M0.2 2.4V5.5M3 2.4V5.7M5.8 2.4V5.9');
      lmSolid(g, 'M5.6 1.1L6 -3H7.4L7.8 1.1Z');                 // the tower
      lmSolid(g, 'M5.3 -3H8.1V-4.5H5.3Z');                      // the lantern
      lmLine(g, 'M6.7 -4.5V-5.7M5.8 -5.2H7.6');
      lmPath(g, 'M9.2 -4L12.4 -4.9M9.2 -3.4L12.4 -2.6', 'sv-lm-ray');
    } },

    /* 2017 variant c — A BREAKWATER: tipped stone with its courses, and
       the sea breaking on the weather side of it. Nothing stands on it. */
    breakwater: { w: 12, draw: function (g) {
      lmPath(g, 'M-12 6.8q10 2.2 20 0M-8 8.2q7 1.6 14 0', 'sv-lm-water');
      lmPath(g, 'M-13 0.2q3 -1.6 5.6 0q2.6 1.6 5.4 0q2.8 -1.6 5.6 0q2.6 1.6 5.2 0', 'sv-lm-water');
      lmSolid(g, 'M-13 4.5L-10.4 1.4H8.4L10.8 4.5Z');
      lmLine(g, 'M-11.6 3H9.6');
      lmLine(g, 'M-9 1.4V4.5M-5.4 1.4V4.5M-1.8 1.4V4.5M1.8 1.4V4.5M5.4 1.4V4.5');
    } },

    /* 2015 — THE CABLE LANDING (LOCKED). LM1: LOW AND WIDE, AND THE
       CABLE IS THE HERO. Tommy of the headland-and-hut this was: *"a
       heart, a white shape and a couple of dashes"* — and Code Point
       stood on the same lump, so the two read as one thing. This is the
       real thing instead: a long low beach, a small cable hut at the back
       of it (Porthcurno), and a thick armoured cable snaking out of the
       hut, down the beach and into the water, where it goes on as the
       chart's own wavy cable line to the foot of the box — which is where
       voyage.js's ruled dashes take it up and carry it across the
       fairway. The cable is a tube (paper between two cuts) hatched
       across like the lay of a rope, so it reads as a heavy line and not
       as a white shape. */
    'cable-hut': { w: 6, noShadow: true, draw: function (g) {
      // the sea off the beach, broken where the cable goes in
      lmPath(g, 'M-14.6 6.2q3.2 1 6.4 0t6.4 0M2 6.2q3 1 6 0t6 0M-11 7.6q3.8 1 7.6 0M2.4 7.6q3.8 1 7.6 0', 'sv-lm-water');
      // the beach: long and low, sloping to the water
      lmSolid(g, 'M-15 4.7V0.2Q-13.4 -0.9 -10.6 -0.8L-3.4 -0.4Q2.6 -0.1 6.8 1.8Q10 3.2 12.6 4.7Z');
      lmPath(g, 'M-14.4 5q6 0.8 12.2 0.1M1.8 5.1q5.4 0.7 10.6 -0.2', 'sv-lm-water');   // the wash on it
      lmLine(g, 'M-14.2 -0.2l0.5 -0.9M-13.4 -0.5l0.4 -1M-5.6 -0.6l0.4 -0.9M-4.6 -0.5l0.5 -0.8' +
                'M3.2 0.2l0.6 -0.8M4.2 0.5l0.5 -0.8');                              // marram on the dune line
      lmPath(g, 'M-6.4 2.6h0.7M-4.4 1.2h0.6M2.4 2.2h0.7M4.4 3.2h0.7M-11.6 3.4h0.7M6.2 2.8h0.6M-8.6 3.8h0.6', 'sv-lm-shadow');
      // the cable hut: small, low, at the back of the beach
      lmSolid(g, 'M-12.4 1.4V-1.6H-8.6V1.4Z');
      lmSolid(g, 'M-13 -1.5L-10.5 -3.7L-8 -1.5Z');
      lmLine(g, 'M-11.2 1.4V-0.4H-10V1.4');
      // THE CABLE, out of the hut's wall, down the beach and into the sea
      var C = tube([[-8.8, 0.7], [-6.8, 1.1], [-5.2, 0.8], [-3.8, 1.4], [-3.3, 2.5],
                    [-2.4, 3.4], [-1.0, 3.8], [-0.2, 4.6], [0, 5.6], [0, 6.2]], 1.7);
      lmSolid(g, C.d);
      lmPath(g, C.lay, 'sv-lm-ray');
      // and the chart's wavy cable line, running off it to sea
      var W = 'M0 6.2q1.3 0.46 0 0.92t0 0.92t0 0.92';
      lmLine(g, W);
      g.save(); g.translate(0.18, 0); lmLine(g, W); g.restore();
    } },

    /* 2009 variant a — A HALF-TIDE ROCK: it shows at half tide and hides
       at high water, which is Tommy's own reading of the two systems
       nobody ever sailed over — *"the perilous rocks of the two systems
       that never got used"*. Two of these stand side by side with a perch
       between them; each carries its own dotted danger ring, drawn on the
       paper at its true extent by voyage.js. */
    'rock-awash': { w: 5, noShadow: true, draw: function (g) {
      lmPath(g, 'M-7 5q7 2.2 14 0M-4.6 6.6q4.6 1.6 9.2 0', 'sv-lm-water');
      lmSolid(g, 'M-4.6 4.6L-2 0.2L-0.6 1.8L1.2 -1.6L3.2 1.8L4.8 4.6Z');
      lmLine(g, 'M-1.6 4.6L-0.6 2.2M2.2 4.6L1.5 1.2');
    } },

    /* 2009 variant a — THE PERCH between them: a pole set in a rock with
       a cone topmark, which is what a surveyor leaves on a danger he has
       found so the next boat sees it. */
    perch: { w: 3, noShadow: true, draw: function (g) {
      lmPath(g, 'M-5 5.2q5 1.8 10 0', 'sv-lm-water');
      lmSolid(g, 'M-2.8 4.6L-0.9 2.4L0.9 3L2.8 4.6Z');
      lmLine(g, 'M0 2.8V-6.4');
      lmSolid(g, 'M0 -6.6L2.8 -5.2L0 -3.8Z');
    } },

    /* 2004 (was 2007, UR1) variant a — A SLIPWAY, AND A BOAT GOING DOWN IT. Starting university is
       a launching: the rails, the ties, and a small boat on her cradle
       part way into the water. */
    slipway: { w: 10, draw: function (g) {
      lmPath(g, 'M-2 6.8q7 2.2 14 0M1 8.2q5 1.6 10 0', 'sv-lm-water');
      lmSolid(g, 'M-15 4.5V2.2H-3.2L2.4 4.5Z');
      lmLine(g, 'M-3.8 2.8L7 6.8M-2 1.9L8.8 5.9');
      lmLine(g, 'M-3 2.4L-1.6 1.9M0 3.5L1.4 3M3 4.6L4.4 4.1M6 5.7L7.4 5.2');
      g.save(); g.translate(2.6, 1.4); g.rotate(0.35);
      lmPath(g, 'M-4.2 0q1 1.9 4.2 1.9t4.2 -1.9q-2 0.7 -4.2 0.7t-4.2 -0.7z', 'sv-ship-hull');
      lmPath(g, 'M-4.2 0H4.2M0 0V-4.4', 'sv-ship-line');
      lmPath(g, 'M0 -4.4l2.6 0.6 -2.6 0.6z', 'sv-ship-flag');
      g.restore();
    } },

    /* 2007 variant b — A PIER WITH A PAVILION at its head: the deck, the
       piles, and a little hipped-roof pavilion with a finial. */
    'pier-pavilion': { w: 12, draw: function (g) {
      lmPath(g, 'M-4 6.4q8 2.2 16 0M-1 7.8q6 1.8 12 0', 'sv-lm-water');
      lmSolid(g, 'M-15 4.5V2.8H-7V4.5Z');
      lmSolid(g, 'M-8 1.2H8.4V2.5H-8Z');
      lmLine(g, 'M-6 2.5V5M-3 2.5V5.3M0 2.5V5.5M3 2.5V5.7M6 2.5V5.9');
      lmSolid(g, 'M1.4 1.2V-2.4H7.4V1.2Z');                     // the pavilion
      lmSolid(g, 'M0.6 -2.4L4.4 -5.4L8.2 -2.4Z');               // its roof
      lmLine(g, 'M4.4 -5.4V-6.8M3 1.2V-1H3.8V1.2M5.4 -0.4H6.4');
    } },

    /* 1998 — CODE POINT (LOCKED): a signal station. LM1: TALL AND
       THIN, AND THE HOIST IS THE HERO. It stood on the cable landing's
       headland with a hut beside it and read as the same heart and white
       shape; now it is a flagstaff on a small rock and nothing else — the
       staff stayed to the rock, its yard, a pennant streaming from the
       truck, and a hoist of four signal flags down the halyard from the
       yardarm, each a different flag (a red, a quartered, a white with a
       red centre, a crossed), flying out on the wind. The pun does the
       work — code sent from a point, aged twelve, and a code point is a
       real thing besides. */
    'signal-station': { w: 4.4, draw: function (g) {
      lmPath(g, 'M-6.4 5.8q3.2 1.3 6.4 0t6.4 0M-4.4 7.2q2.2 1 4.4 0t4.4 0', 'sv-lm-water');
      lmSolid(g, 'M-5.2 4.8L-3.4 2.4L-1.6 2.9L0.4 1.8L2.6 2.8L4.6 4.8Z');     // the point
      lmLine(g, 'M-2.2 4.8L-1.4 3.2M2 4.8L1.4 3.1');
      lmLine(g, 'M-3.6 2.6L0 -9.6L3.4 2.7');                                  // the stays
      lmSolid(g, 'M-0.35 2.2L-0.2 -12.9H0.2L0.35 2.2Z');                      // the staff
      lmLine(g, 'M-4.2 -10.6Q0 -11.3 4.2 -10.6');                             // the yard
      lmCircle(g, 0, -13.2, 0.4, 'sv-vig-line');                             // the truck
      lmPath(g, 'M0.2 -13L6.2 -12L0.2 -11.6Z', 'sv-ship-flag');           // the pennant
      lmLine(g, 'M0.2 -13L6.2 -12L0.2 -11.6');
      // the halyard, from the yardarm down to the staff's foot
      lmLine(g, 'M4.1 -10.6L4.1 -1.6L0.3 1.2');
      // four flags on it, flying out to the east
      lmPath(g, 'M4.1 -10.2H7.3V-8.2H4.1Z', 'sv-ship-flag');                  // red
      lmLine(g, 'M4.1 -10.2H7.3V-8.2H4.1');
      lmSolid(g, 'M4.1 -7.8H7.3V-5.8H4.1Z');                                  // quartered
      lmPath(g, 'M4.1 -7.8H5.7V-6.8H4.1ZM5.7 -6.8H7.3V-5.8H5.7Z', 'sv-ship-flag');
      lmSolid(g, 'M4.1 -5.4H7.3V-3.4H4.1Z');                                  // red centre
      lmPath(g, 'M5.1 -4.8H6.3V-4H5.1Z', 'sv-ship-flag');
      lmSolid(g, 'M4.1 -3H7.3V-1H4.1Z');                                      // crossed
      lmLine(g, 'M4.1 -3L7.3 -1M4.1 -1L7.3 -3');
    } },

    /* 1994 variant b — AN ISLET WITH ONE HOUSE, and one window in it. The
       rock, the house, the chimney; the LIGHT in the window is the cell
       below, drawn over this one only when the sky over London is dark. */
    islet: { w: 8, draw: function (g) {
      lmPath(g, 'M-11 5.4q11 2.6 22 0M-8.4 7q8.4 2 16.8 0', 'sv-lm-water');
      lmSolid(g, 'M-8.6 4.8L-5.4 1.4L-2.2 2.2L1.6 -0.2L5 1.8L8.6 4.8Z');
      lmSolid(g, 'M-2.6 1.6V-2.6L0.8 -5.2L4.2 -2.6V1.6Z');      // the house
      lmLine(g, 'M-3.6 -2.6L0.8 -6L5.2 -2.6M3 -4.2V-5.8H4.2V-3.3');
      // THE ONE WINDOW, dark: an unlit window is a hole in a wall
      lmLine(g, 'M-0.6 -0.2V-2.4H1.8V-0.2ZM0.6 -2.4V-0.2M-0.6 -1.3H1.8');
    } },

    /* ==== V2 FOLLOW-UP: ONE LAMP, BURNING, FOR EVERY LIGHT ON THE
       VOYAGE. Paper, not light: an engraving has no light in it, so a
       thing that is lit is where the ink is left off (X11's rule, and
       the same two inks the lantern at 1986 is lit with). It is drawn
       about its OWN origin and the caller sets it where the lamp is —
       the window of the 1994 islet, the lantern of the 2017 pier light,
       the gallery of the 2007 lighthouse — so three lights cost one cell
       of a twelve-slot atlas instead of three. It replaces `islet-lit`,
       which was that window and nothing else. ==== */
    'lamp-lit': { w: 3, noShadow: true, draw: function (g) {
      lmCircle(g, 0, 0, 4.6, 'sv-lm-glow');
      lmCircle(g, 0, 0, 2.7, 'sv-lm-glow');
      lmCircle(g, 0, 0, 1.25, 'sv-lm-spark');
    } },

    /* 1994 variant c — FISHING STAKES: a line of poles standing in the
       shallow water with the tide running past them. */
    stakes: { w: 9, noShadow: true, draw: function (g) {
      lmPath(g, 'M-11 5.2q11 2.4 22 0M-8 6.8q8 1.8 16 0', 'sv-lm-water');
      lmLine(g, 'M-8.4 5V-3.4M-5.2 5.2V-4.6M-2 5.2V-5.2M1.2 5.2V-4.8M4.4 5V-3.8M7.4 4.8V-2.6');
      lmLine(g, 'M-8.4 -2.6L-5.2 -3.8L-2 -4.4L1.2 -4L4.4 -3L7.4 -1.8');
      lmPath(g, 'M-9.6 3.4q2 -1 3.2 0q1.2 1 3.2 0q2 -1 3.2 0q1.2 1 3.2 0', 'sv-lm-water');
    } }
  };

  /* THE TYPE'S OWN LANDMARK, where the event names none. A rule, in code,
     over the four kinds of day the record knows about. */
  var LM_DEFAULT = {
    holiday: 'tent',                       // a week away takes the house
    'work-trip': 'lighthouse',
    'quarterly-planning': 'stone-circle',
    roundtable: 'stone-circle',           // a moot is a moot (AI Roundtable, Tommy 2026-09-10)
    'aws-summit': 'marquee',
    conference: 'marquee',                // VS1: Vercel Ship, 2026-06-17 — a conference that is not an AWS Summit
    'bank-holiday': 'church'
  };


  /* =================================================================
     THE BUILDINGS (v24, B) — one table, two forms.

     Everything else on the plate has ONE form. A building has two: a plan
     FOOTPRINT on the printed sheet (FP1/LK6: a fine ink outline, knocked
     out of the ground and hatched, as an Ordnance sheet prints it, at mid and near) and a SOLID on the tilted sheet (a small mesh
     lit by the sun, the moon or the lantern, casting a shadow on the
     ground). Neither is authored: both are read off the PARTS below. A
     part is a primitive — box, gable, cone, cyl (cylinder), pyr (pyramid),
     dome — at an offset from the building's own point, in sheet units,
     with a footprint w × d, a base z and a height h. The plan draws every
     part that stands on the ground (z 0) as its rect or its circle; the
     relief instances every part as a mesh. `tone` picks the ink: paper
     walls, a darker roof, grey rock.

     Sizes are in sheet units — a day cell is 40 by 78 — because a
     building is landscape, and landscape scales with the ground. */
  function bPart(s, x, y, w, d, z, h, tone, rot) {
    return { s: s, x: x, y: y, w: w, d: d, z: z, h: h, tone: tone || 'wall', rot: rot || 0 };
  }
  function bHouse(x, y, w, d, h, rot) {
    return [bPart('box', x, y, w, d, 0, h, 'wall', rot), bPart('gable', x, y, w * 1.08, d * 1.12, h, h * 0.62, 'roof', rot)];
  }
  var BUILDINGS = {
    house:  { parts: bHouse(0, 0, 7, 4.5, 2.2) },
    barn:   { parts: bHouse(0, 0, 9.5, 5.2, 2.6) },
    chapel: { parts: bHouse(0, 0, 7.5, 4.6, 2.8).concat([
                bPart('box', -3.2, 0, 1.3, 1.3, 4.5, 1.6, 'wall'),
                bPart('pyr', -3.2, 0, 1.6, 1.6, 6.1, 1.2, 'roof')]) },
    church: { parts: bHouse(2.5, 0, 12, 5.6, 3.4).concat([
                bPart('box', -5.5, 0, 4.6, 4.6, 0, 7.5, 'wall'),
                bPart('pyr', -5.5, 0, 5, 5, 7.5, 5.5, 'roof')]) },
    tower:  { parts: [bPart('box', 0, 0, 4.2, 4.2, 0, 8, 'wall'), bPart('pyr', 0, 0, 4.8, 4.8, 8, 2.4, 'roof')] },
    /* a wall in a farmyard: a low run of stone */
    'yard-wall': { parts: [bPart('box', 0, 0, 9, 0.7, 0, 1.1, 'rock')] },
    /* a lighthouse stands on its rock, which is why it may stand in the sea */
    lighthouse: { parts: [
      bPart('cone', 0, 0, 7, 7, 0, 0.9, 'rock'),
      bPart('cyl', 0, 0, 3.2, 3.2, 0.5, 8.5, 'wall'),
      bPart('cyl', 0, 0, 2.4, 2.4, 9, 1.6, 'ink'),
      bPart('cone', 0, 0, 3, 3, 10.6, 1.2, 'roof')] },
    /* --- the named landmarks that are buildings --------------------- */
    carcassonne: { parts: (function () {
      var out = [], N = 8, R = 9.5, r = 6.2;
      for (var i = 0; i < N; i++) {
        var a = (i + 0.5) / N * Math.PI * 2, L = 2 * R * Math.sin(Math.PI / N);
        out.push(bPart('box', Math.cos(a) * R, Math.sin(a) * R, L * 1.02, 1.1, 0, 3, 'wall', a + Math.PI / 2));
        var b = (i + 0.5) / N * Math.PI * 2, l = 2 * r * Math.sin(Math.PI / N);
        out.push(bPart('box', Math.cos(b) * r, Math.sin(b) * r, l * 1.02, 1, 0, 3.8, 'wall', b + Math.PI / 2));
      }
      for (var k = 0; k < 5; k++) {
        var t = k / 5 * Math.PI * 2 + 0.4;
        out.push(bPart('cyl', Math.cos(t) * R, Math.sin(t) * R, 2.6, 2.6, 0, 4.6, 'wall'));
        out.push(bPart('cone', Math.cos(t) * R, Math.sin(t) * R, 3.2, 3.2, 4.6, 2.8, 'roof'));
      }
      out.push(bPart('box', 0, 0, 4.4, 4.4, 0, 5, 'wall'));
      out.push(bPart('pyr', 0, 0, 5, 5, 5, 2.2, 'roof'));
      return out;
    })() },
    'city-wall': { parts: [
      bPart('box', 0, 0, 16, 1.3, 0, 3, 'wall'),
      bPart('box', 0, 0, 4.4, 3.6, 0, 5.2, 'wall'),
      bPart('pyr', 0, 0, 5, 4.2, 5.2, 1.8, 'roof'),
      bPart('box', -7, 0, 2.4, 2.4, 0, 4, 'wall'),
      bPart('box', 7, 0, 2.4, 2.4, 0, 4, 'wall')] },
    belfry: { parts: [
      bPart('box', 0, 0, 3.6, 3.6, 0, 9.5, 'wall'),
      bPart('cone', 0, 0, 4.2, 4.2, 9.5, 3.4, 'roof'),
      bPart('box', 4.2, 0, 5, 3.6, 0, 2.4, 'wall'),
      bPart('gable', 4.2, 0, 5.4, 4, 2.4, 1.4, 'roof')] },
    dome: { parts: [bPart('cyl', 0, 0, 8, 8, 0, 3.2, 'wall'), bPart('dome', 0, 0, 8, 8, 3.2, 4, 'wall')] },
    /* ==== R5 (c): "what's the black blob on the land?" — THIS. The
       pyramid was the one kit built entirely in the `ink` tone. `ink` is
       the colour the sheet PRINTS A PLAN in, and every kit prints its
       plan in it at tilt 0 whatever its tone (`uSolid` is nought there);
       a part that is toned `ink` keeps that colour when the sheet is
       RAISED as well, and the solid's only shading is a 0.72-1.0 ramp on
       a near-black, so an eleven-unit pyramid stood up as a flat black
       lump on the shore of the island. It is `wall` now, like every other
       kit's body: the plan it prints at tilt 0 is the same black
       footprint to the pixel (the plan is drawn from `vPlan`, and `wall`
       and `ink` both print), and raised it is a shaded stone pyramid.
       The lighthouse keeps its one ink part — a black lantern gallery two
       units high reads as ink and is meant to. ==== */
    pyramid: { parts: [bPart('pyr', 0, 0, 11, 11, 0, 7.5, 'wall')] },
    'ruined-columns': { parts: [
      bPart('box', 0, 0, 7, 3, 0, 0.6, 'rock'),
      bPart('cyl', -2, 0, 1.1, 1.1, 0.6, 5, 'wall'),
      bPart('cyl', 2, 0, 1.1, 1.1, 0.6, 5, 'wall'),
      bPart('box', 0.6, 0, 3.4, 1.3, 5.6, 0.8, 'wall')] },
    /* LM2: the buildings among EV2's holidays. The palm, the gondola, the
       sea stack and the crag under Edinburgh's castle are not buildings;
       the torii's plan is its two posts and the tower's its four feet. */
    torii: { parts: [
      bPart('cyl', -2.6, 0, 0.8, 0.8, 0, 5.2, 'wall'),
      bPart('cyl', 2.6, 0, 0.8, 0.8, 0, 5.2, 'wall'),
      bPart('box', 0, 0, 6.2, 0.5, 3.8, 0.5, 'wall'),
      bPart('box', 0, 0, 7.6, 0.9, 5.2, 0.7, 'roof')] },
    eiffel: { parts: [
      bPart('box', -2.3, -2.3, 1, 1, 0, 2.6, 'wall'),
      bPart('box', 2.3, -2.3, 1, 1, 0, 2.6, 'wall'),
      bPart('box', -2.3, 2.3, 1, 1, 0, 2.6, 'wall'),
      bPart('box', 2.3, 2.3, 1, 1, 0, 2.6, 'wall'),
      bPart('box', 0, 0, 5.8, 5.8, 2.6, 0.5, 'wall'),
      bPart('pyr', 0, 0, 4.4, 4.4, 3.1, 11, 'wall')] },
    duomo: { parts: bHouse(-4.5, 0, 11, 5, 3.4).concat([
      bPart('cyl', 1.5, 0, 5.4, 5.4, 0, 5.4, 'wall'),
      bPart('dome', 1.5, 0, 5.4, 5.4, 5.4, 4.2, 'roof'),
      bPart('cyl', 1.5, 0, 1, 1, 9.6, 1.4, 'wall'),
      bPart('box', 7.2, 3.6, 2.4, 2.4, 0, 9.5, 'wall')]) },
    'mill-chimney': { parts: [
      bPart('box', -1.5, 0, 11, 5, 0, 4.6, 'wall'),
      bPart('box', -4.5, 0, 1.8, 1.8, 4.6, 1.2, 'wall'),
      bPart('cyl', 6, 0, 1.7, 1.7, 0, 12, 'wall')] },
    windmill: { parts: [
      bPart('cone', 0, 0, 4, 4, 0, 1, 'wall'),
      bPart('cyl', 0, 0, 3.4, 3.4, 0, 6, 'wall'),
      bPart('cone', 0, 0, 3.8, 3.8, 6, 1.8, 'roof')] },
    parliament: { parts: [
      bPart('box', 0, 0, 16, 3.6, 0, 3.2, 'wall'),
      bPart('gable', -5, 0, 6.2, 3.8, 3.2, 1.6, 'roof'),
      bPart('gable', 5, 0, 6.2, 3.8, 3.2, 1.6, 'roof'),
      bPart('cyl', 0, 0, 4.4, 4.4, 0, 5, 'wall'),
      bPart('dome', 0, 0, 4.6, 4.6, 5, 2.8, 'roof'),
      bPart('cone', 0, 0, 0.9, 0.9, 7.8, 3.4, 'roof'),
      bPart('box', -4.6, 0, 1.2, 1.2, 0, 5.4, 'wall'),
      bPart('box', 4.6, 0, 1.2, 1.2, 0, 5.4, 'wall'),
      bPart('pyr', -4.6, 0, 1.3, 1.3, 5.4, 2.6, 'roof'),
      bPart('pyr', 4.6, 0, 1.3, 1.3, 5.4, 2.6, 'roof')] },
    minaret: { parts: [
      bPart('box', 0, 0, 9, 9, 0, 3.2, 'wall'),
      bPart('cyl', 0, 0, 6.4, 6.4, 3.2, 1.2, 'wall'),
      bPart('dome', 0, 0, 6.4, 6.4, 4.4, 3.2, 'roof'),
      bPart('cyl', -6.6, -4, 0.9, 0.9, 0, 10, 'wall'),
      bPart('cyl', 6.6, 4, 0.9, 0.9, 0, 10, 'wall'),
      bPart('cone', -6.6, -4, 1, 1, 10, 2.8, 'roof'),
      bPart('cone', 6.6, 4, 1, 1, 10, 2.8, 'roof')] },
    castle: { parts: [
      bPart('box', 0, -1, 4.6, 4.6, 0, 6.4, 'wall'),
      bPart('box', 0, 3.2, 12, 1.2, 0, 3.2, 'wall'),
      bPart('cyl', -6.2, 3.2, 2.4, 2.4, 0, 4.2, 'wall'),
      bPart('cyl', 6.2, 3.2, 2.4, 2.4, 0, 4.2, 'wall')] },
    giralda: { parts: [
      bPart('box', 0, 0, 3.8, 3.8, 0, 9.6, 'wall'),
      bPart('box', 0, 0, 3.2, 3.2, 9.6, 2.2, 'wall'),
      bPart('box', 0, 0, 2.2, 2.2, 11.8, 1.4, 'wall'),
      bPart('dome', 0, 0, 1.8, 1.8, 13.2, 1.2, 'roof')] }
  };
  /* WHICH LANDMARKS ARE BUILDINGS. These stand as solids on the tilted
     sheet and print a footprint on the flat one; every other landmark (the
     geyser, Vesuvius, the wall with sheep, the tram, the rabelo, the
     tent, the marquee, the stone circle, the cairns) stays a standing
     sprite, because it is not a building or is too small to be one. */
  var LM_BUILDING = {
    house: 'house', church: 'church', lighthouse: 'lighthouse',
    carcassonne: 'carcassonne', 'city-wall': 'city-wall', belfry: 'belfry',
    dome: 'dome', pyramid: 'pyramid', 'ruined-columns': 'ruined-columns',
    torii: 'torii', eiffel: 'eiffel', duomo: 'duomo', 'mill-chimney': 'mill-chimney',
    windmill: 'windmill', parliament: 'parliament', minaret: 'minaret',
    castle: 'castle', giralda: 'giralda'
  };
  /* WHAT A SETTLEMENT IS MADE OF, as offsets along and across the strip in
     sheet units, from the flattest cell of the settled run. A rule, not a
     plan: the same farmstead every time, the way a sheet uses the same
     symbol every time. */
  var SETTLEMENTS = {
    barn:      { box: [12, 8],  lay: [['barn', 0, 0, 0]] },
    farmstead: { box: [24, 14], lay: [['house', 0, 0, 0], ['barn', 10.5, -2.5, 0], ['yard-wall', 5, 4.6, 0]] },
    hamlet:    { box: [30, 22], lay: [['chapel', 0, 0, 0], ['house', -10.5, -4, 0], ['house', 10.5, -5, 0], ['house', 2, 7.5, Math.PI / 2]] },
    village:   { box: [40, 30], lay: [['church', 0, 0, 0], ['house', -14, -8, 0], ['house', 13, -7, 0],
                                      ['house', -13, 7, Math.PI / 2], ['house', 13, 8, 0], ['house', -1, -12, 0],
                                      ['barn', 4, 12.5, 0], ['yard-wall', -6, 12.5, 0]] }
  };

  /* A SHADOW ON THE GROUND SIDE. Three short rules laid to the south-east,
     which is where every shadow on this sheet falls (the relief is lit from
     the north-west and has been since v1). An engraver would not have
     airbrushed it; he would have cut three lines and stopped. */
  function lmShadow(w) {
    return 'M' + (w * 0.22).toFixed(1) + ' 5.1H' + (w * 1.16).toFixed(1) +
      'M' + (w * 0.46).toFixed(1) + ' 6H' + (w * 0.98).toFixed(1) +
      'M' + (w * 0.7).toFixed(1) + ' 6.9H' + (w * 0.88).toFixed(1);
  }

  /* A SKERRY, so that a landmark in open water still stands on rock.
     v9 ruled that no vignette is cut in open sea, and it was right: a house
     floating on the water is a lie about the ground. But most of Tommy's
     2025 leave falls below the datum, and a lighthouse on a rock is the
     oldest thing on any chart. So where the landmark's own point of ground
     is under water, a skerry is drawn first and the landmark stands on it —
     which makes it a coast, and the rule is honoured rather than excepted.
     The rock is part of the STANDING object, not the ground: it is a
     vignette of rock, cut in ink, not a contour traced from the record. */
  function lmSkerry(g) {
    lmPath(g, 'M-13 5.4q13 3 26 0', 'sv-lm-water');
    lmPath(g, 'M-10.4 7q10.4 2.6 20.8 0', 'sv-lm-water');
    lmSolid(g, 'M-9.6 5.2L-6 1.6L-4.4 2.6L-2.2 0.1H2.2L4.6 2.4L6.2 1.4L9.6 5.2Z');
    lmLine(g, 'M-4.6 5.2L-3 2.4M3.2 5.2L1.8 2.2M-1 5.2L-0.2 3');
  }

  /* A RIPPLE, for anything that floats (v23, C): two short reflections on
     the water under the hull, in the sea's own ink, so that in plan a boat
     reads as ON the water and not printed over it. */
  function lmRipple(g, w) {
    var a = (w * 1.3).toFixed(1), b = (w * 0.9).toFixed(1);
    lmPath(g, 'M-' + a + ' 5.2q' + a + ' 1.6 ' + (a * 2) + ' 0M-' + b + ' 6.6q' + b + ' 1.4 ' + (b * 2) + ' 0', 'sv-lm-water');
  }

  /* PUT ONE ON THE GROUND. Returns false where the key names nothing — an
     unknown `landmark` in the data leaves the sheet alone rather than
     drawing a question mark on it.

     `mode` says what it stands on (v23, C and D): 'land' (its own point of
     ground); 'skerry' (a rock drawn under it, for the lighthouse and nothing
     else now); 'afloat' (a floating thing, on the water surface at the
     datum — the caller stands it on the water plane and not on the bed);
     'bed' (ground furniture: the wreck, draped into the texture and seen
     through the water, laid at `angle`, in DEGREES as on the sheet). The
     mode used to be a boolean `inSea`, and true still means the skerry.

     The context's origin is already at the landmark's anchor and its scale
     is already set — including the apparent-size hold (.sv-lm-body's
     scale(1.85 / z)), which is placement, not artwork. */
  function drawLandmark(g, key, mode, angle) {
    var lm = LANDMARKS[key];
    if (!lm) return false;
    if (mode === true) mode = 'skerry';
    if (lm.bed) mode = 'bed';
    else if (lm.floats && mode !== 'land') mode = 'afloat';
    var wasBed = bedMode;
    bedMode = (mode === 'bed');
    g.save();
    if (mode === 'skerry') {
      lmSkerry(g);
      // stood on the rock's own plateau, not on the water it breaks
      g.translate(0, -4.4);
    } else if (mode === 'afloat') {
      lmRipple(g, lm.w);
    } else if (mode === 'bed' && angle) {
      g.rotate(angle * Math.PI / 180);
    }
    if (!lm.noShadow && mode !== 'bed') lmPath(g, lmShadow(lm.w), 'sv-lm-shadow');
    lm.draw(g);
    g.restore();
    bedMode = wasBed;
    return true;
  }

  /* ==== R5 (d): THE COMPASS ROSE, AND WHY IT IS HERE AND NOT ON THE PAPER
     The sheet's rose stands INSIDE the neat line. E1 and E9 both rastered
     the margin into one furniture texture printed on the paper, and the
     engine draws the ground OVER the paper, so the rose was painted out —
     measured at 0 px of ink surviving (REPORT-room-E9.md 1.4). E9 cut it
     from the texture and named the mechanism: it belongs to the layer the
     marks live on, cut at runtime from the sheet's own art.

     This is that art, lifted path for path out of v32's `#sheet-over`
     (`room-e9/v32-over.svg`, the last dump the old site will ever give)
     with the rose's own centre subtracted, so the numbers below are the
     printed rose to four places. Its units are the OVERLAY's screen px,
     which the sheet is 0.770727 of — the caller multiplies.

     A sixteen-point rose: four long points to the cardinals, eight short
     ones between, each split lit/dark down its axis so the star reads as
     a solid turned to the light; two rings; a hub; a fleur-de-lis on the
     north point, which is the whole reason it turns with the bearing. ==== */
  var ROSE = {
    x0: -53, y0: -60.5, x1: 53, y1: 53,   /* the art's box, in rose units */
    draw: function (g) {
      function ring(r, w, op) {
        g.save(); g.globalAlpha = op; g.strokeStyle = '#4a7079'; g.lineWidth = w;
        g.beginPath(); g.arc(0, 0, r, 0, Math.PI * 2); g.stroke(); g.restore();
      }
      function face(d, fill, stroke, w, op, join) {
        g.save(); g.globalAlpha = op; g.lineJoin = join || 'miter';
        var pth = new Path2D(d);
        g.fillStyle = fill; g.fill(pth);
        g.strokeStyle = stroke; g.lineWidth = w; g.stroke(pth);
        g.restore();
      }
      ring(51.4462, 1, 0.85);
      ring(47.0678, 0.5, 0.6);
      /* the eight short points, lit half then dark half */
      face('M0 0L0 -5.2979L9.2155 -22.2481ZM0 0L3.7462 -3.7462L22.2481 -9.2155ZM0 0L5.2979 0L22.2481 9.2155ZM0 0L3.7462 3.7462L9.2155 22.2481ZM0 0L0 5.2979L-9.2155 22.2481ZM0 0L-3.7462 3.7462L-22.2481 9.2155ZM0 0L-5.2979 0L-22.2481 -9.2155ZM0 0L-3.7462 -3.7462L-9.2155 -22.2481Z',
           '#f7efdd', '#37585f', 0.5, 0.96);
      face('M0 0L3.7462 -3.7462L9.2155 -22.2481ZM0 0L5.2979 0L22.2481 -9.2155ZM0 0L3.7462 3.7462L22.2481 9.2155ZM0 0L0 5.2979L9.2155 22.2481ZM0 0L-3.7462 3.7462L-9.2155 22.2481ZM0 0L-5.2979 0L-22.2481 9.2155ZM0 0L-3.7462 -3.7462L-22.2481 -9.2155ZM0 0L0 -5.2979L-9.2155 -22.2481Z',
           '#37585f', '#37585f', 0.4, 0.88);
      /* the four long points to the cardinals */
      face('M0 0L-6.9815 -6.9815L0 -44.8786ZM0 0L6.9815 -6.9815L44.8786 0ZM0 0L6.9815 6.9815L0 44.8786ZM0 0L-6.9815 6.9815L-44.8786 0Z',
           '#f7efdd', '#37585f', 0.5, 0.96);
      face('M0 0L6.9815 -6.9815L0 -44.8786ZM0 0L6.9815 6.9815L44.8786 0ZM0 0L-6.9815 6.9815L0 44.8786ZM0 0L-6.9815 -6.9815L-44.8786 0Z',
           '#37585f', '#37585f', 0.4, 0.88);
      g.save(); g.fillStyle = '#37585f';
      g.beginPath(); g.arc(0, 0, 2.4, 0, Math.PI * 2); g.fill(); g.restore();
      face('M0 -44.8786l-3.4 -7 3.4 -3.4 3.4 3.4zM-3.4 -51.8786q-4 -3 -1 -7q3 3 1 7M3.4 -51.8786q4 -3 1 -7q-3 3 -1 7',
           '#f7efdd', '#37585f', 0.8, 1, 'round');
    }
  };
  api.ROSE = ROSE;

  api.LANDMARKS = LANDMARKS;
  api.LM_DEFAULT = LM_DEFAULT;
  api.LM_BUILDING = LM_BUILDING;
  api.BUILDINGS = BUILDINGS;
  api.SETTLEMENTS = SETTLEMENTS;
  api.bPart = bPart;
  api.drawLandmark = drawLandmark;
  api.widthOf = function (key) {
    var lm = LANDMARKS[key];
    return lm ? lm.w : 0;
  };
  root.RoomLmArt = api;
})(typeof self !== 'undefined' ? self : this);
